import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import './App.css';
import WalletConnection from './components/WalletConnection';
import WalletSelectionModal from './components/WalletSelectionModal';
import Dashboard from './components/Dashboard';
import TabbedOperations from './components/TabbedOperations';
import InviteGate from './components/InviteGate';
import FeedbackModal from './components/FeedbackModal';
import { AuthService } from './services/authService';
import { secureKeyCache } from './services/secureKeyCache.js';
import { formatPlainForDisplay, normalizeTokenDecimals } from './utils/confidentialAmountScale.js';
import {
    connectWithWalletConnect,
    connectWithMetaMaskSDK,
    disconnectWalletConnect,
    getWalletConnectProvider,
    getMetaMaskSDKProvider,
    connectWithMetaMask,
    connectWithTrustWallet,
    connectWithOKXWallet,
    connectWithCoinbaseWallet,
    connectWithRainbowWallet,
    restoreStellarWallet,
    disconnectStellarWalletSession,
    getMetaMaskProvider as getMetaMaskProviderFromService,
    connectWithSpecificEip1193Provider,
    discoverEip6963Providers,
    getCoinbaseWalletProvider,
    getRainbowWalletProvider,
} from './services/walletService';
import {
    ensureKitInitialized,
    listSupportedStellarWallets,
    connectStellarWalletById,
    kitWalletIdToAppOrigin,
} from './services/stellarWalletsKit';
// Background image is now handled by CSS

// Import WebAssembly module
import init, { 
    encrypt_amount, 
    decrypt_ciphertext,
    generate_transfer_proof,
    generate_withdraw_proof
} from './pkg/confidential_transfer_proof_generation.js';

// Helpers (top-level to reduce nesting inside component)
const formatAddress = (address) => `${address.slice(0, 6)}...${address.slice(-4)}`;

// Safer notification IDs with counter to avoid collisions
let __nid = 0;
function pushNotification(setNotifications, message, type = 'info', persistent = false) {
    const id = `${Date.now()}-${++__nid}`;
    setNotifications((prev) => [...prev, { id, message, type, persistent }]);
    // Only auto-dismiss if not persistent
    if (!persistent) {
        // Error messages stay longer to give users time to read them
        const timeout = type === 'error' ? 15000 : 10000;
        setTimeout(() => {
            setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, timeout);
    }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Import chain configurations
import { CHAINS, STELLAR_LAB_FUND_ACCOUNT_URL } from './config/chains';
import { createStellarGatewayContractFacade } from './utils/stellarGatewayContract';

/** Set to true to require an invite code before using the app. */
const INVITE_GATE_ENABLED = false;

// EVM network configs for MetaMask `wallet_addEthereumChain` (unused in Stellar-only build)
const NETWORK_CONFIGS = {};
const STELLAR_MAINNET_HORIZON_URL = 'https://horizon.stellar.org';
const REQUIRED_STELLAR_ASSET_CODES = ['USDC', 'EURC'];
const REQUIRED_STELLAR_XLM_BALANCE = Number(process.env.REACT_APP_STELLAR_REQUIRED_XLM_BALANCE || 2);

const sanitizeHorizonBaseUrl = (url) => String(url || '').replace(/\/+$/, '');

const fetchHorizonAccount = async (horizonUrl, publicKey) => {
    const safeBase = sanitizeHorizonBaseUrl(horizonUrl);
    if (!safeBase || !publicKey) {
        return { found: false, status: null, data: null };
    }
    const endpoint = `${safeBase}/accounts/${publicKey}`;
    try {
        const res = await fetch(endpoint);
        if (res.status === 404) {
            return { found: false, status: 404, data: null };
        }
        if (!res.ok) {
            return { found: false, status: res.status, data: null };
        }
        const data = await res.json();
        return { found: true, status: res.status, data };
    } catch (error) {
        console.warn('[Stellar preflight] Horizon lookup failed:', endpoint, error);
        return { found: false, status: null, data: null };
    }
};

const parseBalance = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const isConfiguredForStellarTestnet = () => {
    const passphrase = String(CHAINS?.stellar?.networkPassphrase || '').trim().toLowerCase();
    return passphrase.includes('test');
};

const validateStellarAccountPreflight = async (publicKey) => {
    const testnetHorizon = CHAINS?.stellar?.horizonUrl;
    if (!publicKey || !testnetHorizon) {
        return null;
    }

    const [testnetAccount, mainnetAccount] = await Promise.all([
        fetchHorizonAccount(testnetHorizon, publicKey),
        isConfiguredForStellarTestnet() ? fetchHorizonAccount(STELLAR_MAINNET_HORIZON_URL, publicKey) : Promise.resolve(null),
    ]);

    if (!testnetAccount?.found && mainnetAccount?.found) {
        return 'Your wallet account appears on Stellar Mainnet. Please switch your wallet network to Stellar Testnet and reconnect.';
    }

    if (!testnetAccount?.found) {
        return 'NO_STELLAR_TESTNET_ACCOUNT';
    }

    const balances = Array.isArray(testnetAccount?.data?.balances) ? testnetAccount.data.balances : [];
    const native = balances.find((b) => b?.asset_type === 'native');
    const xlmBalance = parseBalance(native?.balance);
    if (xlmBalance < REQUIRED_STELLAR_XLM_BALANCE) {
        return `Your Stellar Testnet account needs at least ${REQUIRED_STELLAR_XLM_BALANCE} XLM for reserve/fees (current: ${xlmBalance}). Please top up and reconnect.`;
    }

    const trustlines = balances.filter((b) => b?.asset_type && b.asset_type !== 'native');
    const hasRequiredTrustline = trustlines.some((b) =>
        REQUIRED_STELLAR_ASSET_CODES.includes(String(b?.asset_code || '').toUpperCase())
    );
    if (!hasRequiredTrustline) {
        return `No ${REQUIRED_STELLAR_ASSET_CODES.join('/')} trustline was found on this Stellar Testnet account. Add a trustline, then reconnect.`;
    }

    const requiredTokenBalance = trustlines
        .filter((b) => REQUIRED_STELLAR_ASSET_CODES.includes(String(b?.asset_code || '').toUpperCase()))
        .reduce((sum, b) => sum + parseBalance(b?.balance), 0);
    if (requiredTokenBalance <= 0) {
        return `Your ${REQUIRED_STELLAR_ASSET_CODES.join('/')} balance is zero on Stellar Testnet. Fund the account with test tokens, then reconnect.`;
    }

    return null;
};

const rebuildContractForActiveChain = async ({
    activeProvider,
    updateProvider,
    updateSigner,
    updateContract,
    updateChainId,
    updateChainPreference,
}) => {
    if (!activeProvider) {
        console.warn('[rebuildContractForActiveChain] No active provider available');
        return;
    }

    try {
        const ethersProvider = new ethers.providers.Web3Provider(activeProvider, 'any');
        const signer = ethersProvider.getSigner();
        const network = await ethersProvider.getNetwork();
        const chainIdNum = Number(network.chainId);

        const chainEntry = Object.entries(CHAINS).find(([, cfg]) => cfg.chainIdDecimal === chainIdNum);
        const chainCfg = chainEntry?.[1];
        const chainKey = chainEntry?.[0];

        updateProvider(ethersProvider);
        updateSigner(signer);
        updateChainId(chainIdNum);

        if (chainCfg?.contractAddress) {
            const newContract = new ethers.Contract(chainCfg.contractAddress, CONTRACT_ABI, signer);
            updateContract(newContract);
            console.log('[rebuildContractForActiveChain] Contract set to', chainCfg.contractAddress, 'for', chainCfg.name);
        } else {
            updateContract(null);
            console.warn('[rebuildContractForActiveChain] No contract address configured for chainId', chainIdNum);
        }

        if (chainKey) {
            updateChainPreference(chainKey);
        }
    } catch (error) {
        console.error('[rebuildContractForActiveChain] Failed to rebuild contract:', error);
    }
};

// Legacy MetaMask add-network param (Stellar-only: not applicable)
const ARBITRUM_SEPOLIA = null;

// Helper to convert decimal chain ID to hex
const toHexChainId = (dec) => '0x' + Number(dec).toString(16);

// Helper to pick network config by decimal chain ID
const pickNetworkConfigByDecimal = (dec) => {
    return Object.values(NETWORK_CONFIGS).find((config) => {
        if (!config?.chainId) return false;
        const parsed = parseInt(config.chainId, 16);
        return !Number.isNaN(parsed) && parsed === dec;
    }) || null;
};

// Helper to resolve RPC URL based on chain ID
const resolveRpcUrl = (decChainId) => {
    const config = pickNetworkConfigByDecimal(decChainId);
    return config?.rpcUrls?.[0] || null;
};

// Helper to check if a value is zero (handles BigNumber, string, number)
const isZeroish = (v) => {
    if (v == null) return true;
    try { return ethers.BigNumber.from(v).isZero(); } catch { return Number(v) === 0; }
};

// Helper to format units (handles BigNumber, string, number)
const fmt = (v, dec = 18) => {
    try { return ethers.utils.formatUnits(v, dec); } catch { return String(v); }
};

// Helper function to detect if running on mobile device
const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (typeof window !== 'undefined' && window.innerWidth <= 768);
};

// Helper function to detect iOS specifically
// Note: Chrome on iOS is still iOS, just with a different user agent
const isIOS = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
           (/iPad/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
};

// Helper function to detect Android specifically
const isAndroid = () => {
    return /Android/i.test(navigator.userAgent);
};

// Helper function to detect if we're in MetaMask's in-app browser
const isInMetaMaskBrowser = () => {
    if (typeof window === 'undefined' || !window.ethereum) {
        return false;
    }
    
    // Check if MetaMask is available and user agent indicates mobile/MetaMask browser
    return window.ethereum.isMetaMask && 
           (navigator.userAgent.includes('MetaMask') || 
            (navigator.userAgent.includes('Mobile') && window.ethereum.isMetaMask));
};

// Helper function to detect desktop browser
const isDesktop = () => {
    return !isMobileDevice();
};

// Helper function to open MetaMask app via deep link
// Works on iOS (Safari, Chrome) and Android (Chrome, Firefox, etc.)
const openMetaMaskApp = (currentUrl) => {
    const isIOSDevice = isIOS();
    const isAndroidDevice = isAndroid();
    
    if (!isIOSDevice && !isAndroidDevice) {
        return false; // Not mobile, no deep linking needed
    }
    
    try {
        // Clean and normalize the URL
        let urlToOpen = currentUrl || window.location.href;
        
        // Remove hash fragments - they can cause blank page issues in MetaMask
        if (urlToOpen.includes('#')) {
            urlToOpen = urlToOpen.split('#')[0];
        }
        
        // Remove query parameters that might cause issues
        // Keep only essential ones, but for now just use base URL
        // Actually, we should keep query params as they might be needed
        // Just remove hash
        
        // Ensure URL is absolute
        if (!urlToOpen.startsWith('http://') && !urlToOpen.startsWith('https://')) {
            urlToOpen = window.location.origin + (urlToOpen.startsWith('/') ? urlToOpen : '/' + urlToOpen);
        }
        
        console.log('Original URL:', currentUrl);
        console.log('Cleaned URL for MetaMask:', urlToOpen);
        
        // MetaMask uses Universal Links for mobile
        // Format: https://link.metamask.io/dapp/{dappUrl}
        // CRITICAL: For iOS Chrome, we need to use query parameter format to avoid double-encoding
        // The path format can cause double-encoding issues in iOS Chrome
        
        let universalLink;
        const isIOSChrome = isIOSDevice && navigator.userAgent.includes('Chrome');
        
        if (isIOSChrome) {
            // iOS Chrome: Use query parameter format to avoid encoding issues
            // Format: https://link.metamask.io/dapp?url={dappUrl}
            // This prevents double-encoding that causes blank pages
            universalLink = `https://link.metamask.io/dapp?url=${encodeURIComponent(urlToOpen)}`;
            console.log(`iOS Chrome: Using query parameter format to avoid double-encoding:`, universalLink);
        } else {
            // Other browsers: Use path format (standard)
            const encodedUrl = encodeURIComponent(urlToOpen);
            universalLink = `https://link.metamask.io/dapp/${encodedUrl}`;
            console.log(`Using standard path format (${isIOSDevice ? 'iOS' : 'Android'}):`, universalLink);
        }
        
        // For iOS Chrome specifically, use window.open with query format
        // This prevents the double-encoding issue that causes blank pages
        if (isIOSChrome) {
            try {
                // Use window.open with query format - this works best for iOS Chrome
                const opened = window.open(universalLink, '_blank');
                if (opened) {
                    console.log('iOS Chrome: Successfully opened MetaMask with query format');
                    return true;
                }
            } catch (e) {
                console.log('iOS Chrome: window.open failed, trying fallback:', e);
            }
            
            // Fallback: Try path format if query format didn't work
            try {
                const pathFormat = `https://link.metamask.io/dapp/${encodeURIComponent(urlToOpen)}`;
                console.log('iOS Chrome: Trying path format fallback:', pathFormat);
                const opened = window.open(pathFormat, '_blank');
                if (opened) {
                    console.log('iOS Chrome: Successfully opened with path format');
                    return true;
                }
            } catch (e) {
                console.log('iOS Chrome: Path format also failed:', e);
            }
        }
        
        // Method 1: Create a hidden link element and click it
        // This works well in Safari and other browsers (not iOS Chrome)
        if (!isIOSChrome) {
            const link = document.createElement('a');
            link.href = universalLink;
            link.style.display = 'none';
            link.target = '_self'; // Use _self for better compatibility in mobile browsers
            document.body.appendChild(link);
            
            // Try clicking the link
            link.click();
            
            // Clean up after a short delay
            setTimeout(() => {
                if (document.body.contains(link)) {
                    document.body.removeChild(link);
                }
            }, 500);
        }
        
        return true;
    } catch (error) {
        console.error('Error opening MetaMask app:', error);
        // Fallback: try direct deep link scheme
        try {
            let urlToOpen = currentUrl || window.location.href;
            if (urlToOpen.includes('#')) {
                urlToOpen = urlToOpen.split('#')[0];
            }
            if (!urlToOpen.startsWith('http://') && !urlToOpen.startsWith('https://')) {
                urlToOpen = window.location.origin + (urlToOpen.startsWith('/') ? urlToOpen : '/' + urlToOpen);
            }
            
            const encodedUrl = encodeURIComponent(urlToOpen);
            // Try platform-specific deep links
            const deepLink = `metamask://dapp?url=${encodedUrl}`;
            console.log('Trying direct deep link fallback:', deepLink);
            
            // Try multiple methods for direct deep link
            if (isIOSDevice && navigator.userAgent.includes('Chrome')) {
                // For iOS Chrome, try window.open first
                try {
                    window.open(deepLink, '_blank');
                    return true;
                } catch (e) {
                    console.log('window.open for deep link failed:', e);
                }
            }
            
            // Fallback to window.location
            window.location.href = deepLink;
            return true;
        } catch (fallbackError) {
            console.error('Fallback deep link also failed:', fallbackError);
            return false;
        }
    }
};

// Helper function to wait for MetaMask to be available (especially important on mobile)
const waitForMetaMask = async (maxWaitMs = 3000, checkIntervalMs = 100) => {
    const startTime = Date.now();
    
    while (Date.now() - startTime < maxWaitMs) {
        if (typeof window !== 'undefined' && window.ethereum) {
            // Check if it's MetaMask or has MetaMask in providers
            if (window.ethereum.isMetaMask) {
                return window.ethereum;
            }
            if (window.ethereum.providers) {
                const metaMask = window.ethereum.providers.find(p => p.isMetaMask);
                if (metaMask) return metaMask;
            }
        }
        await new Promise(resolve => setTimeout(resolve, checkIntervalMs));
    }
    
    return null;
};

// Helper function to get MetaMask provider specifically
// Use the strict version from walletService - ensures no fallback to other wallets
const getMetaMaskProvider = getMetaMaskProviderFromService;

// ---- BLOCKLIST: ban Phantom EVM provider everywhere ----
const isPhantomProvider = (prov) => {
    if (!prov) return false;
    
    // direct flags commonly set by Phantom
    if (prov.isPhantom || prov.isPhantomEvm || prov.isPhantomEthereum) return true;
    
    // EIP-6963 metadata (some libs expose info/_info)
    const rdns = prov.info?.rdns || prov._info?.rdns || '';
    const name = prov.info?.name || prov._info?.name || '';
    if (/phantom/i.test(rdns) || /phantom/i.test(name)) return true;
    
    // stringify fallback
    const s = (prov.toString?.() || '').toLowerCase();
    return s.includes('phantom');
};

// ConfidentialMirror ABI (minimal used by the app)
const CONTRACT_ABI = [
    "function createConfidentialAccount(bytes elgamalPubkey) external",
    "function deposit(address token, uint256 plainAmount) external",
    "function getAccountCore(address ownerAddr) external view returns ((bool,bool,bool,uint64,bytes,uint64,uint64))",
    "function getPendingAction(address ownerAddr) external view returns ((uint8,address,uint256,address,address,uint256,uint256,bool,bytes))",
    "function debugCanSubmitFlags(address owner) external view returns (bool exists, bool finalized, bool pendingAction, bool manualResult, bool canSubmitResult)",
    "function getAvailable(address ownerAddr, address token) external view returns (bytes c1, bytes c2)",
    "function getPending(address ownerAddr, address token) external view returns (bytes c1, bytes c2)",
    "function transferConfidential(address recipient, address token, bytes proof, bool useOffchainVerify) external payable",
    "function withdraw(address token, uint256 plainAmount, bytes proof, bool useOffchainVerify) external",
    "function applyPending() external",
    "function feeToken() external view returns (address)",
    "function feeAmount() external view returns (uint256)",
    // Events
    "event CreateAccountRequested(address indexed ownerAddr, uint256 indexed txId, bytes elgamalPubkey)",
    "event CreateAccountProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, bytes elgamalPubkey)",
    "event DepositRequested(address indexed ownerAddr, uint256 indexed txId, address indexed token, uint256 plainAmount)",
    "event DepositProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, address token, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter, uint64 minimumPendingCreditCounter, bytes availableShare, bytes pendingShare)",
    "event TransferRequested(address indexed sender, address indexed recipient, uint256 indexed txId, address token, bytes proof, uint256 feePaid, bool useOffchainVerify, bytes senderPubkey, bytes recipientPubkey, bytes senderCurrC1, bytes senderCurrC2)",
    "event TransferProcessed(address indexed sender, address indexed recipient, uint256 indexed txId, bool ok, string errorMsg, address token, bytes senderNewAvailC1, bytes senderNewAvailC2, bytes recipientNewPendingC1, bytes recipientNewPendingC2, uint64 senderPendingCreditCounter, bytes senderAvailableShare, uint64 recipientPendingCreditCounter, uint64 recipientMinimumPendingCreditCounter, bytes recipientPendingShare)",
    "event ApplyPendingRequested(address indexed ownerAddr, uint256 indexed txId)",
    "event ApplyPendingProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, address[] tokens, bytes[] newAvailC1, bytes[] newAvailC2, uint64 pendingCreditCounter, uint64 minimumPendingCreditCounter, bytes availableShare)",
    "event WithdrawRequested(address indexed ownerAddr, uint256 indexed txId, address token, uint256 plainAmount, bytes proof, bool useOffchainVerify, bytes userPubkey, bytes userCurrC1, bytes userCurrC2)",
    "event WithdrawProcessed(address indexed ownerAddr, uint256 indexed txId, bool ok, string errorMsg, address token, uint256 plainAmount, bytes newAvailC1, bytes newAvailC2, uint64 pendingCreditCounter, uint64 minimumPendingCreditCounter, bytes availableShare)"
];

// Remember which wallet was last active across page reloads
const WALLET_STORAGE_KEY = 'stabletrust:lastWalletOrigin';
// SessionStorage key to track if tab is still open (persists on refresh, clears on tab close)
const WALLET_SESSION_KEY = 'stabletrust:walletTabActive';
// LocalStorage key to track if user explicitly disconnected (persists across refreshes)
const WALLET_DISCONNECTED_KEY = 'stabletrust:walletDisconnected';

const persistLastWallet = (origin) => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        if (!origin) {
            window.localStorage.removeItem(WALLET_STORAGE_KEY);
        } else {
            window.localStorage.setItem(WALLET_STORAGE_KEY, origin);
        }
    } catch (e) {
        console.warn('[WalletPersist] Failed to persist last wallet origin:', e);
    }
};

const readLastWallet = () => {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
        const origin = window.localStorage.getItem(WALLET_STORAGE_KEY);
        return origin || null;
    } catch {
        return null;
    }
};

const clearLastWallet = () => {
    persistLastWallet(null);
};

/**
 * Browser-extension (non-EVM) Stellar wallet origins. Every wallet the app
 * supports is now funneled through the Stellar Wallets Kit, so any origin
 * produced by `kitWalletIdToAppOrigin` should be treated as Stellar here.
 */
const STELLAR_BROWSER_WALLET_ORIGINS = new Set([
    'freighter',
    'hana',
    'xbull',
    'lobstr',
    'albedo',
    'rabet',
    'stellar',
]);
function isStellarBrowserWalletOrigin(origin) {
    return !!origin && STELLAR_BROWSER_WALLET_ORIGINS.has(origin);
}

// Set sessionStorage flag to indicate wallet is connected in this tab
// This flag persists on page refresh but clears when tab is closed
const setWalletTabActive = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        window.sessionStorage.setItem(WALLET_SESSION_KEY, 'true');
        console.log('[WalletTab] Session flag set - wallet will remain connected on refresh');
    } catch (e) {
        console.warn('[WalletTab] Failed to set session flag:', e);
    }
};

// Check if the tab is still active (was not closed)
const isWalletTabActive = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    try {
        const isActive = window.sessionStorage.getItem(WALLET_SESSION_KEY) === 'true';
        console.log('[WalletTab] Session flag check:', isActive ? 'tab is active (refresh)' : 'tab was closed');
        return isActive;
    } catch {
        return false;
    }
};

// Clear sessionStorage flag when wallet disconnects
const clearWalletTabActive = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        window.sessionStorage.removeItem(WALLET_SESSION_KEY);
        console.log('[WalletTab] Session flag cleared');
    } catch (e) {
        console.warn('[WalletTab] Failed to clear session flag:', e);
    }
};

// Set localStorage flag to indicate user explicitly disconnected
const setWalletDisconnectedFlag = () => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(WALLET_DISCONNECTED_KEY, 'true');
        console.log('[WalletDisconnect] Disconnected flag set - wallet will not auto-reconnect on refresh');
    } catch (e) {
        console.warn('[WalletDisconnect] Failed to set disconnected flag:', e);
    }
};

// Check if user explicitly disconnected
const isWalletDisconnected = () => {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    try {
        const isDisconnected = window.localStorage.getItem(WALLET_DISCONNECTED_KEY) === 'true';
        console.log('[WalletDisconnect] Disconnected flag check:', isDisconnected ? 'user disconnected' : 'not disconnected');
        return isDisconnected;
    } catch {
        return false;
    }
};

// Clear disconnected flag when wallet connects
const clearWalletDisconnectedFlag = () => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.removeItem(WALLET_DISCONNECTED_KEY);
        console.log('[WalletDisconnect] Disconnected flag cleared');
    } catch (e) {
        console.warn('[WalletDisconnect] Failed to clear disconnected flag:', e);
    }
};

function useAppState() {
    const state = {
        wasmModule: useState(null),
        provider: useState(null),
        signer: useState(null),
        contract: useState(null),
        userAccount: useState(null),
        hasActiveAccount: useState(false),
        accountExists: useState(false),
        accountData: useState(null),
        notifications: useState([]),
        keyBannerVisible: useState(false),
        derivedKeys: useState(null),
        isAuthenticated: useState(false),
        confidentialBalance: useState(null),
        activeTab: useState('transfer'),
        bannerVisible: useState(true),
        currentTokenData: useState(null),
    };
    return state;
}

function App() {
    // Chain/token selection now persists across page refreshes
    // (previously had one-time cleanup that removed selectedChainId - now keeping it)
    const {
        wasmModule: [wasmModule, setWasmModule],
        provider: [provider, setProvider],
        signer: [signer, setSigner],
        contract: [contract, setContract],
        userAccount: [userAccount, setUserAccount],
        hasActiveAccount: [hasActiveAccount, setHasActiveAccount],
        accountExists: [accountExists, setAccountExists],
        accountData: [accountData, setAccountData],
        notifications: [notifications, setNotifications],
        keyBannerVisible: [keyBannerVisible, setKeyBannerVisible],
        derivedKeys: [derivedKeys, setDerivedKeys],
        isAuthenticated: [isAuthenticated, setIsAuthenticated],
        confidentialBalance: [confidentialBalance, setConfidentialBalance],
        activeTab: [activeTab, setActiveTab],
        bannerVisible: [bannerVisible, setBannerVisible],
        currentTokenData: [currentTokenData, setCurrentTokenData],
    } = useAppState();
    
    /** False while the first `getAccountCore` (or equivalent) for the current connection is in flight — avoids consent UI that assumes "no account" before chain data arrives. */
    const [accountCoreSynced, setAccountCoreSynced] = useState(true);

    const [pendingBalance, setPendingBalance] = useState(null);
    const [pendingBalanceBannerDismissed, setPendingBalanceBannerDismissed] = useState(false);
    const prevPendingBalanceRef = useRef(null);
    const [pendingActionKind, setPendingActionKind] = useState(null); // Track pending action kind: 2=deposit, 3=transfer, 5=withdraw
    const [onSecureClaimClick, setOnSecureClaimClick] = useState(null);
    const [secureClaimInFlightByKey, setSecureClaimInFlightByKey] = useState({});
    const [currentChainId, setCurrentChainId] = useState(null);
    // Shared wallet selection modal state
    const [showWalletSelectionModal, setShowWalletSelectionModal] = useState(false);
    const [availableWalletsForModal, setAvailableWalletsForModal] = useState(null);
    const [availableStellarWallets, setAvailableStellarWallets] = useState([]);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    
    // --- Invite Gate Access Control ---
    const [hasAccess, setHasAccess] = useState(() => {
        if (!INVITE_GATE_ENABLED) return true;
        try {
            return localStorage.getItem('stabletrust:hasAccess') === 'true';
        } catch {
            return false;
        }
    });

    const handleAccessGranted = useCallback(() => {
        setHasAccess(true);
    }, []);
    
    // Track if we're in the process of connecting to a specific wallet (to prevent auto-connect)
    const isConnectingToWalletRef = useRef(false);
    /** True only while Privy's `login()` modal promise is pending — cleared in `login` finally so Connect works right after dismiss (X). */
    const privyLoginModalInFlightRef = useRef(false);
    /** True while wiring the embedded wallet after auth (waits, chain switch, ethers) — blocks duplicate full connects. */
    const privyWalletHookupInFlightRef = useRef(false);
    /** Bumped on each explicit Privy connect so an older connect can exit waits quietly when the user retries. */
    const privyConnectGenerationRef = useRef(0);
    /** Bumped on each Connect click for Privy so an older handleWalletSelect finally does not clear state while a newer attempt runs. */
    const privyConnectUiAttemptRef = useRef(0);
    
    // Prevent any auto-connect after a cancelled Privy flow
    // Start blocked by default; only unblock after a successful connect
    const suppressAutoConnectRef = useRef(true);
    
    // Track the active connect target to prevent cross-wallet interference
    const CONNECT_TARGETS = {
        NONE: null,
        PRIVY: 'privy',
        FREIGHTER: 'freighter',
        HANA: 'hana',
        METAMASK: 'metamask',
        TRUST: 'trust',
        OKX: 'okx',
        COINBASE: 'coinbase',
        RAINBOW: 'rainbow',
    };
    const [connectTarget, setConnectTarget] = useState(CONNECT_TARGETS.NONE);
    // Refs for live values in event listeners (fixes stale closure problem)
    const connectTargetRef = useRef(connectTarget);
    useEffect(() => { connectTargetRef.current = connectTarget; }, [connectTarget]);
    
    const updateConnectTarget = useCallback((valueOrUpdater) => {
        setConnectTarget((prev) => {
            const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
            connectTargetRef.current = next;
            return next;
        });
    }, [setConnectTarget]);
    
    const [activeWalletOrigin, setActiveWalletOrigin] = useState(null);
    const activeWalletOriginRef = useRef(activeWalletOrigin);
    useEffect(() => { activeWalletOriginRef.current = activeWalletOrigin; }, [activeWalletOrigin]);
    
    // Track explicit “I disconnected Privy, don’t auto-reconnect” state
    const manualPrivyDisconnectRef = useRef(false);
    
    const [walletConnectProvider, setWalletConnectProvider] = useState(null);
    const [metaMaskSDKProvider, setMetaMaskSDKProvider] = useState(null);
    const [activeEip1193, setActiveEip1193] = useState(null); // Track the active EIP-1193 provider
    const activeEip1193Ref = useRef(activeEip1193);
    useEffect(() => { activeEip1193Ref.current = activeEip1193; }, [activeEip1193]);
    
    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen((prev) => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    const handleSecureClaimInFlightChange = useCallback((inFlight, claimKey) => {
        setSecureClaimInFlightByKey((prev) => {
            const next = { ...prev };
            // If no key provided, clear all in-flight secure claims for the current user
            if (!claimKey) {
                if (!userAccount) return next;
                const prefix = `${userAccount.toLowerCase()}::`;
                Object.keys(next).forEach((k) => {
                    if (k.startsWith(prefix)) delete next[k];
                });
                return next;
            }
            if (inFlight) next[claimKey] = true;
            else delete next[claimKey];
            return next;
        });
    }, [userAccount]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleResize = () => {
            if (window.innerWidth > 768) {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const showWalletSelectionModalRef = useRef(showWalletSelectionModal);
    useEffect(() => { showWalletSelectionModalRef.current = showWalletSelectionModal; }, [showWalletSelectionModal]);
    
    const [lastConnectedAccount, setLastConnectedAccount] = useState(null); // Track last notified account to prevent duplicates
    const [lastNotifiedChain, setLastNotifiedChain] = useState(null); // Track last notified chain to prevent duplicates
    
    // Privy hooks
    const { ready: privyReady, authenticated, login, createWallet, getAccessToken } = usePrivy();
    const { wallets: privyWallets, ready: walletsReady } = useWallets();

    // Track Privy readiness in a ref so async code sees updates
    const privyReadyRef = useRef(privyReady);
    useEffect(() => { privyReadyRef.current = privyReady; }, [privyReady]);

    const privyWalletsRef = useRef(privyWallets);
    useEffect(() => { privyWalletsRef.current = privyWallets; }, [privyWallets]);
    const authenticatedRef = useRef(authenticated);
    useEffect(() => { authenticatedRef.current = authenticated; }, [authenticated]);
    const walletsReadyRef = useRef(walletsReady);
    useEffect(() => { walletsReadyRef.current = walletsReady; }, [walletsReady]);
    const createWalletRef = useRef(createWallet);
    useEffect(() => { createWalletRef.current = createWallet; }, [createWallet]);

    // Dismiss the initial page loader (defined in public/index.html) once Privy
    // has finished initializing. Falls back to a hard timeout so the loader can
    // never get stuck if Privy hangs (e.g. offline / misconfigured env).
    useEffect(() => {
        const markAppReady = () => {
            if (typeof document !== 'undefined' && !document.body.classList.contains('app-ready')) {
                document.body.classList.add('app-ready');
                const loader = document.getElementById('app-loader');
                if (loader) {
                    // Remove from DOM after the CSS fade-out completes
                    setTimeout(() => { loader.parentNode && loader.parentNode.removeChild(loader); }, 700);
                }
            }
        };

        if (privyReady) {
            // Small delay lets the UI paint one frame before the loader fades,
            // avoiding a visible flash of grey placeholders.
            const t = setTimeout(markAppReady, 120);
            return () => clearTimeout(t);
        }

        // Safety net: never block the UI behind the loader for more than 6s
        const failSafe = setTimeout(markAppReady, 6000);
        return () => clearTimeout(failSafe);
    }, [privyReady]);

    // Track previous Privy authenticated value so we can detect the moment
    // the user goes from "logged out" -> "logged in" and auto-wire the wallet.
    const prevAuthenticatedRef = useRef(false);
    
    // Refs to store functions so they can be accessed before they're defined
    const checkConfidentialAccountRef = useRef(null);
    const setWalletConnectedWithProviderRef = useRef(null);
    const setWalletDisconnectedRef = useRef(null);
    const setWalletConnectedRef = useRef(null);
    const userAccountRef = useRef(null);
    
    const providerRef = useRef(null);
    useEffect(() => {
        providerRef.current = provider;
    }, [provider]);

    const signerRef = useRef(null);
    useEffect(() => {
        signerRef.current = signer;
    }, [signer]);
    
    // Centralized provider event handler registry
    const providerHandlersRef = useRef(new Map());
    
    // Global guard: allow connection only if not suppressed or explicit target matches origin
    const allowConnectNow = useCallback((origin) => {
        // ─────────────────────────────────────────────────────────────
        // 1) MetaMask is *only* allowed when:
        //    - user explicitly selected MetaMask (connectTarget === METAMASK), OR
        //    - MetaMask is already the active wallet (activeWalletOrigin === METAMASK).
        //    It must NOT auto-attach when Privy (or any other wallet) is active.
        // ─────────────────────────────────────────────────────────────
        if (origin === 'metamask') {
            const activeOrigin = activeWalletOriginRef.current;
            const target = connectTargetRef.current;

            // If some other wallet (e.g. Privy) is active, MetaMask must never steal focus
            if (activeOrigin && activeOrigin !== CONNECT_TARGETS.METAMASK) {
                console.log('[Guard] MetaMask connect blocked: active wallet =', activeOrigin);
                return false;
            }

            // Initial connect or reconnect is only allowed during an explicit MetaMask flow
            if (target === CONNECT_TARGETS.METAMASK) {
                return true;
            }

            // Once MetaMask is already the active wallet, allow its own events
            if (activeOrigin === CONNECT_TARGETS.METAMASK) {
                return true;
            }

            // In all other cases (no active wallet & no explicit MM flow) -> block
            console.log('[Guard] MetaMask connect blocked: no explicit MetaMask flow');
            return false;
        }

        if (isStellarBrowserWalletOrigin(origin)) {
            const activeOrigin = activeWalletOriginRef.current;
            const target = connectTargetRef.current;
            if (activeOrigin && activeOrigin !== origin) {
                return false;
            }
            if (target === origin) {
                return true;
            }
            if (activeOrigin === origin) {
                return true;
            }
            return false;
        }

        // ─────────────────────────────────────────────────────────────
        // 2) Non-MetaMask origins keep the existing behaviour
        // ─────────────────────────────────────────────────────────────
        if (!suppressAutoConnectRef.current) {
            return true;
        }

        const activeTarget = connectTargetRef.current;
        if (!activeTarget) {
            return false;
        }

        const map = {
            privy: CONNECT_TARGETS.PRIVY,
            freighter: CONNECT_TARGETS.FREIGHTER,
            hana: CONNECT_TARGETS.HANA,
            metamask: CONNECT_TARGETS.METAMASK,
            trust: CONNECT_TARGETS.TRUST,
            okx: CONNECT_TARGETS.OKX,
            coinbase: CONNECT_TARGETS.COINBASE,
            rainbow: CONNECT_TARGETS.RAINBOW,
        };

        return map[origin] === activeTarget;
    }, []);
    
    // Register event handlers for a provider (with suppression guards)
    const registerHandlers = useCallback((prov, handlers, origin) => {
        if (!prov?.on) return;
        
        // Wrap handlers to respect suppression (except disconnect, which always runs for cleanup)
        const wrap = (fn) => fn
            ? (...args) => {
                if (!allowConnectNow(origin)) {
                    console.log('[Guard]', origin, 'event ignored (auto-connect suppressed)');
                    return;
                }
                try { fn(...args); } catch (e) { console.error('handler error', e); }
            }
            : undefined;
        
        const wrapped = {
            accountsChanged: wrap(handlers.accountsChanged),
            chainChanged: wrap(handlers.chainChanged),
            // allow disconnect without the guard so we always clean up
            disconnect: handlers.disconnect,
        };
        
        if (wrapped.accountsChanged) prov.on('accountsChanged', wrapped.accountsChanged);
        if (wrapped.chainChanged) prov.on('chainChanged', wrapped.chainChanged);
        if (wrapped.disconnect) prov.on('disconnect', wrapped.disconnect);
        
        // keep the *wrapped* refs so we can remove the exact functions later
        providerHandlersRef.current.set(prov, wrapped);
    }, [allowConnectNow]);
    
    // Unregister event handlers for a provider
    const unregisterHandlers = useCallback((prov) => {
        if (!prov?.removeListener) return;
        const wrapped = providerHandlersRef.current.get(prov);
        if (!wrapped) return;
        if (wrapped.accountsChanged) prov.removeListener('accountsChanged', wrapped.accountsChanged);
        if (wrapped.chainChanged) prov.removeListener('chainChanged', wrapped.chainChanged);
        if (wrapped.disconnect) prov.removeListener('disconnect', wrapped.disconnect);
        providerHandlersRef.current.delete(prov);
    }, []);
    
    // Get WalletConnect Project ID from environment variable
    const WALLETCONNECT_PROJECT_ID = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
    
    // Trigger EIP-6963 discovery early so cache gets populated before first connect
    useEffect(() => {
        // Fire-and-forget; this populates _announced cache
        discoverEip6963Providers(1200).catch(() => {});
    }, []);

    // Helper function to check if we're using Privy and reconnect if needed
    // Defined early so it can be used in useEffect and other functions
    // Note: checkConfidentialAccount is referenced but defined later, so we'll access it via closure
    // This function can now do a first connection, not only "reconnect after user already had a wallet attached"
    const ensurePrivyConnection = useCallback(async () => {
        // 🔒 Do NOT auto-reconnect Privy if the user explicitly disconnected it
        if (manualPrivyDisconnectRef.current) {
            console.log('[Privy] Auto-connect suppressed due to manual disconnect');
            return false;
        }
        
        // 🔒 Do NOT auto-reconnect if user explicitly disconnected (persists across refreshes)
        if (isWalletDisconnected()) {
            console.log('[Privy] Auto-connect suppressed - user explicitly disconnected');
            return false;
        }
        
        // 🔒 Do NOT auto-reconnect if tab was closed and reopened (only reconnect on refresh)
        // Note: First-time login is handled by the auto-connect effect which sets setWalletTabActive()
        // when justLoggedIn is detected, so by the time this function is called, the flag should be set.
        if (!isWalletTabActive()) {
            console.log('[Privy] Auto-connect suppressed - tab was closed (not a refresh)');
            return false;
        }

        if (isStellarBrowserWalletOrigin(readLastWallet())) {
            console.log('[Privy] ensurePrivyConnection skipped — persisted last wallet is Stellar (Freighter/Hana)');
            return false;
        }

        // Only run when Privy is ready, user is logged in, and wallets are loaded
        if (!privyReady || !authenticated || !privyWallets || privyWallets.length === 0) {
            return false;
        }

        // Use the same robust wallet resolution as connectWithPrivy
        // CRITICAL: Only use embedded wallets, NEVER fall back to external wallets (MetaMask, OKX, etc.)
        // This prevents reconnecting to the wrong wallet type on page refresh
        const resolveEmbeddedWallet = (wallets) =>
            (wallets || []).find((w) => {
                if (w?.isEmbedded) return true;
                const clientType = w?.walletClientType?.toLowerCase?.() || '';
                if (clientType.includes('privy')) return true;
                const connectorType = w?.walletConnector?.type?.toLowerCase?.() || '';
                return connectorType === 'privy' || connectorType === 'embedded';
            }) || null;

        const privyWallet = resolveEmbeddedWallet(privyWallets);
        if (!privyWallet) {
            console.log('[Privy] No embedded wallet found - skipping auto-connect (external wallets not allowed for Privy restore)');
            return false;
        }

        // If we already have a wired provider/signer with the same account, nothing to do
        if (userAccount && provider && signer) {
            try {
                const accounts = await provider.listAccounts();
                if (
                    accounts.length > 0 &&
                    accounts[0].toLowerCase() === userAccount.toLowerCase()
                ) {
                    return true;
                }
            } catch (e) {
                console.log('Privy reconnect check failed, rebuilding connection:', e);
            }
        }

        // --- existing connection logic stays the same from here down ---
        try {
            // Ensure correct chain only if user has selected one
            const selected = sessionStorage.getItem('selectedChainId');
            if (selected && NETWORK_CONFIGS[selected]?.chainId) {
                const targetDec = parseInt(NETWORK_CONFIGS[selected].chainId, 16);
                try {
                    await privyWallet.switchChain(targetDec);
                } catch (switchError) {
                    // Handle chain switching errors, especially for unsupported chains
                    if (switchError?.message?.includes('Unsupported chainId') || 
                        switchError?.message?.includes('unsupported chain')) {
                        const chainName = NETWORK_CONFIGS[selected]?.chainName || selected;
                        console.error(`[Privy] Chain switch failed: ${switchError.message}`);
                        showNotification(
                            `Privy does not support ${chainName}. Please use a different wallet or chain.`,
                            'error'
                        );
                        throw switchError;
                    }
                    throw switchError;
                }
            }

            // Get EIP-1193 provider and wrap with ethers
            const eip1193 = await privyWallet.getEthereumProvider();
            const newProvider = new ethers.providers.Web3Provider(eip1193, 'any');
            const newSigner = newProvider.getSigner();
            const accounts = await newProvider.listAccounts();
            if (!accounts?.length) {
                return false;
            }

            setActiveEip1193(eip1193);
            // Always wire the embedded wallet, even on the very first connection.
            // Call setWalletConnectedWithProvider directly instead of through the ref,
            // so it works even when the ref hasn't been set yet (first connection).
            await setWalletConnectedWithProvider(
                accounts[0],
                newProvider,
                newSigner,
                'privy',
                { forceAllow: true }   // Allow Privy to connect even when auto-connect is suppressed
            );

            // Register handlers for future account/chain changes
            registerHandlers(
                eip1193,
                {
                    accountsChanged: (accs) => {
                        if (accs.length === 0) {
                            setWalletDisconnectedRef.current?.();
                            return;
                        }
                        const currentProvider = newProvider;
                        const currentSigner = newSigner;
                        if (currentProvider && currentSigner) {
                            setWalletConnectedWithProviderRef.current?.(
                                accs[0],
                                currentProvider,
                                currentSigner,
                                'privy'
                            );
                        } else {
                            setWalletConnectedRef.current?.(accs[0], 'privy');
                        }
                    },
                    chainChanged: (chainId) => {
                        const newChainId = parseInt(chainId, 16);
                        setCurrentChainId(newChainId);
                        setTimeout(() => {
                            checkConfidentialAccountRef.current?.();
                        }, 1000);
                    },
                },
                'privy'
            );

            setTimeout(() => {
                checkConfidentialAccountRef.current?.();
            }, 500);

            return true;
        } catch (error) {
            console.error('Failed to reconnect Privy:', error);
            return false;
        }
    }, [
        privyReady,
        authenticated,
        privyWallets,
        userAccount,
        provider,
        signer,
        registerHandlers,
    ]);

    // Monitor Privy wallet state and auto-reconnect if needed (especially after account creation)
    useEffect(() => {
        // Only run if Privy is ready and authenticated
        if (!privyReady || !authenticated || !walletsReady) {
            return;
        }

        if (isStellarBrowserWalletOrigin(activeWalletOriginRef.current)) {
            return;
        }

        if (isStellarBrowserWalletOrigin(readLastWallet())) {
            return;
        }

        // Check if we have Privy wallets but no active connection
        if (privyWallets && privyWallets.length > 0) {
            // If we have a userAccount but no provider/signer, we need to reconnect
            if (userAccount && (!provider || !signer)) {
                console.log('Privy wallet detected but connection lost, attempting to reconnect...');
                // Small delay to avoid race conditions
                const timeoutId = setTimeout(() => {
                    ensurePrivyConnection().catch(err => {
                        console.error('Auto-reconnect failed:', err);
                    });
                }, 500);
                
                return () => clearTimeout(timeoutId);
            }
        }
    }, [privyReady, authenticated, walletsReady, privyWallets, userAccount, provider, signer, ensurePrivyConnection]);

    // After Privy auth completes (Google/email/passkey), automatically wire the embedded wallet
    // so that "login with Privy" is a single step: auth + embedded wallet connected.
    //
    // IMPORTANT: we now auto-connect in ANY state where:
    //   - privyReady && walletsReady && authenticated
    //   - and there is NO userAccount wired yet.
    //
    // That covers both:
    //   - "just logged in now", and
    //   - "was already logged in when the page loaded".
    useEffect(() => {
        const prevAuth = prevAuthenticatedRef.current;

        const updatePrev = () => {
            prevAuthenticatedRef.current = authenticated;
        };

        // We need the Privy SDK and wallet list to be ready
        if (!privyReady || !walletsReady) {
            updatePrev();
            return;
        }

        if (isStellarBrowserWalletOrigin(activeWalletOriginRef.current)) {
            updatePrev();
            return;
        }

        if (isStellarBrowserWalletOrigin(readLastWallet())) {
            console.log('[Privy] Skipping embedded auto-connect — last wallet was Freighter/Hana (restoring from storage)');
            updatePrev();
            return;
        }

        // If we're not authenticated with Privy, nothing to do
        if (!authenticated) {
            updatePrev();
            return;
        }

        // If connectWithPrivy() is actively running, let it handle the connection
        // Don't interfere with the explicit connection flow
        if (privyLoginModalInFlightRef.current || privyWalletHookupInFlightRef.current) {
            console.log('[Privy] connectWithPrivy() is running, skipping auto-connect effect');
            updatePrev();
            return;
        }
        
        // 🔒 Do not auto-wire the embedded wallet if the user explicitly disconnected it
        if (manualPrivyDisconnectRef.current) {
            console.log('[Privy] Skipping auto-connect because user manually disconnected Privy');
            updatePrev();
            return;
        }
        
        // 🔒 Do NOT auto-reconnect if user explicitly disconnected (persists across refreshes)
        // BUT: if we just authenticated (first-time signup), allow connection
        const justLoggedIn = authenticated && !prevAuth;
        if (isWalletDisconnected() && !justLoggedIn) {
            console.log('[Privy] Skipping auto-connect - user explicitly disconnected');
            updatePrev();
            return;
        }
        
        // If user just logged in (first-time signup), clear the disconnect flag to allow connection
        if (justLoggedIn && isWalletDisconnected()) {
            console.log('[Privy] First-time login detected, clearing disconnect flag to allow connection');
            clearWalletDisconnectedFlag();
        }
        
        // For first-time login, ensure the tab active flag is set early
        // This ensures ensurePrivyConnection won't be blocked
        if (justLoggedIn) {
            setWalletTabActive();
        }

        // If an EVM account is already wired, don't touch anything
        if (userAccount) {
            updatePrev();
            return;
        }

        // No Privy wallets yet (edge case) -> nothing to wire
        if (!privyWallets || privyWallets.length === 0) {
            updatePrev();
            return;
        }

        // justLoggedIn was already declared above
        console.log('[Privy] Auto-connect: authenticated =', authenticated,
            'prevAuth =', prevAuth,
            'justLoggedIn =', justLoggedIn,
            'hasAccount =', !!userAccount);

        // At this point we KNOW:
        //   - Privy is ready
        //   - Wallets are ready
        //   - User is authenticated
        //   - No EVM account is wired yet
        // → Always ensure the embedded wallet is connected.
        ensurePrivyConnection().catch((err) => {
            console.error('Auto Privy connect failed:', err);
        });

        updatePrev();
    }, [
        privyReady,
        walletsReady,
        authenticated,
        privyWallets,
        userAccount,
        ensurePrivyConnection,
    ]);

    // Initialize WebAssembly module
    useEffect(() => {
        const initializeWasm = async () => {
            try {
                const wasm = await init();
                setWasmModule(wasm);
                console.log('WebAssembly module loaded successfully');
            } catch (error) {
                console.error('Failed to load WebAssembly module:', error);
                showNotification('WebAssembly module failed to load. Some features may not work.', 'error');
            }
        };

        initializeWasm();
    }, []);

    // Initialize Firebase Auth
    useEffect(() => {
        const unsubscribe = AuthService.onAuthStateChanged((user) => {
            setIsAuthenticated(!!user);
            if (user) {
                console.log('User authenticated:', user.uid);
            } else {
                console.log('User not authenticated');
            }
        });

        return () => unsubscribe();
    }, []);

    // Auto-sign in when wallet connects
    useEffect(() => {
        if (userAccount && !isAuthenticated) {
            const signInUser = async () => {
                try {
                    await AuthService.signInAnonymously();
                    await AuthService.updateUserProfile(userAccount);
                    // Authentication successful - no notification needed
                } catch (error) {
                    console.error('Failed to authenticate:', error);
                    showNotification('Failed to authenticate with Firebase', 'error');
                }
            };
            signInUser();
        }
    }, [userAccount, isAuthenticated]);

    // Set up wallet event listeners on mount (but don't auto-connect)
    useEffect(() => {
        // CRITICAL: Check localStorage FIRST to see if Privy was the last wallet
        // If so, set connectTarget immediately to prevent MetaMask from connecting
        const lastWallet = readLastWallet();
        if (lastWallet === CONNECT_TARGETS.PRIVY) {
            console.log('[MM Listeners] Privy was last wallet - setting connectTarget to block MetaMask');
            connectTargetRef.current = CONNECT_TARGETS.PRIVY;
            updateConnectTarget(CONNECT_TARGETS.PRIVY);
        }
        if (isStellarBrowserWalletOrigin(lastWallet)) {
            console.log('[MM Listeners] Stellar extension was last wallet - setting connectTarget to block MetaMask');
            connectTargetRef.current = lastWallet;
            updateConnectTarget(lastWallet);
        }

        // Stellar-only build: no EVM `NETWORK_CONFIGS` — never install MetaMask EIP-1193 listeners.
        // Otherwise `accountsChanged` can fire before Hana restore finishes and steal the session.
        if (Object.keys(NETWORK_CONFIGS).length === 0) {
            console.log('[MM Listeners] No EVM networks configured — skipping MetaMask listener setup');
            return () => {};
        }
        
        let ethereum = null;
        let handleAccountsChanged = null;
        let handleChainChanged = null;
        
        // On mobile, wait for MetaMask to be available before setting up listeners
        const setupListeners = async () => {
            const isMobile = isMobileDevice();
            
            if (isMobile) {
                // Wait for MetaMask on mobile (it may load asynchronously)
                ethereum = await waitForMetaMask(3000, 200);
                if (!ethereum) {
                    ethereum = getMetaMaskProvider();
                }
            } else {
                ethereum = getMetaMaskProvider();
            }
            
            if (!ethereum) {
                return; // No MetaMask available
            }
            
            // Double-check connectTarget before attaching listeners - if Privy is being restored,
            // don't attach listeners that could trigger MetaMask connection
            const currentTarget = connectTargetRef.current;
            if (currentTarget === CONNECT_TARGETS.PRIVY) {
                console.log('[MM Listeners] Skipping listener attachment - Privy is being restored');
                return;
            }
            if (isStellarBrowserWalletOrigin(currentTarget)) {
                console.log('[MM Listeners] Skipping listener attachment - Stellar extension session');
                return;
            }
            
            // Listen for account and chain changes in MetaMask
            handleAccountsChanged = (accounts) => {
                if (isStellarBrowserWalletOrigin(connectTargetRef.current)) {
                    console.log('[MM] accountsChanged ignored: Stellar extension session');
                    return;
                }

                // Ignore MetaMask completely when some other wallet (Privy, OKX, etc.) is the active one
                if (
                    activeWalletOriginRef.current &&
                    activeWalletOriginRef.current !== CONNECT_TARGETS.METAMASK
                ) {
                    console.log('[MM] accountsChanged ignored: active wallet is', activeWalletOriginRef.current);
                    return;
                }

                // If a different explicit connect flow is currently running (e.g. Privy),
                // don't let MetaMask interfere (especially important during wallet restoration on page refresh).
                const target = connectTargetRef.current;
                if (target && target !== CONNECT_TARGETS.METAMASK) {
                    console.log('[MM] accountsChanged ignored: connectTarget=', target, '(another wallet is being restored)');
                    return;
                }

                // Before any wallet has connected, we *do* want to suppress MetaMask
                // auto-attaching just because the user flips accounts.
                if (suppressAutoConnectRef.current && !userAccountRef.current) {
                    console.log('[MM] accountsChanged ignored: auto-connect suppressed (no active userAccount yet)');
                    return;
                }

                if (isConnectingToWalletRef.current || showWalletSelectionModalRef.current) {
                    console.log('[MM] accountsChanged ignored: selection in progress/modal open');
                    return;
                }

                // At this point, MetaMask *is* the active wallet (or the only one),
                // and the user explicitly changed accounts ⇒ always update.
                if (!accounts || accounts.length === 0) {
                    console.log('[MM] accountsChanged → no accounts, disconnecting');
                    setWalletDisconnected();
                    return;
                }

                const next = accounts[0];
                console.log('[MM] accountsChanged → switching to', next);
                setWalletConnected(next, CONNECT_TARGETS.METAMASK);
            };

            handleChainChanged = async (chainIdValue) => {
                if (isStellarBrowserWalletOrigin(connectTargetRef.current)) {
                    console.log('[MM] chainChanged ignored: Stellar extension session');
                    return;
                }

                // Ignore MetaMask chain changes when another wallet is active
                if (
                    activeWalletOriginRef.current &&
                    activeWalletOriginRef.current !== CONNECT_TARGETS.METAMASK
                ) {
                    console.log('[MM] chainChanged ignored: active wallet is', activeWalletOriginRef.current);
                    return;
                }

                // If we haven't allowed any wallet yet and no account is wired, ignore.
                if (suppressAutoConnectRef.current && !userAccountRef.current) {
                    console.log('[MM] chainChanged ignored: auto-connect suppressed (no active userAccount yet)');
                    return;
                }

                // If a different explicit connect flow is currently running (e.g. Privy),
                // don't let MetaMask interfere.
                const target = connectTargetRef.current;
                if (target && target !== CONNECT_TARGETS.METAMASK) {
                    console.log('[MM] chainChanged ignored: connectTarget=', target);
                    return;
                }

                console.log('[MM] Chain changed to:', chainIdValue);

                const dec =
                    typeof chainIdValue === 'string' && chainIdValue.startsWith('0x')
                        ? parseInt(chainIdValue, 16)
                        : Number(chainIdValue);

                setCurrentChainId(dec);

                const ethereumProvider = getMetaMaskProvider(); // same source as above
                const newProvider = new ethers.providers.Web3Provider(ethereumProvider, 'any');
                setProvider(newProvider);
                const newSigner = newProvider.getSigner();
                setSigner(newSigner);

                const chainEntry = Object.entries(CHAINS).find(
                    ([, cfg]) => cfg.chainIdDecimal === dec
                );
                const chainKey = chainEntry?.[0];
                const chainCfg = chainEntry?.[1];

                if (chainKey) {
                    try {
                        sessionStorage.setItem('selectedChainId', chainKey);
                        console.log('Saved chain preference:', chainKey);
                    } catch (e) {
                        console.error('Failed to save chain preference:', e);
                    }
                }

                const nextAddress = chainCfg?.contractAddress || null;
                if (nextAddress) {
                    const nextContract = new ethers.Contract(nextAddress, CONTRACT_ABI, newSigner);
                    setContract(nextContract);
                    console.log('Switched contract to', nextAddress, 'for', chainCfg?.name);
                    if (lastNotifiedChain !== dec) {
                        showNotification(`Switched to ${chainCfg?.name}`, 'success');
                        setLastNotifiedChain(dec);
                    }
                } else {
                    setContract(null);
                    console.warn('No contractAddress configured for', chainCfg?.name || 'this chain');
                    // Removed user-facing warning about contract configuration - not relevant to end users
                    if (lastNotifiedChain !== dec && chainCfg) {
                        showNotification(`Switched to ${chainCfg.name}`, 'success');
                        setLastNotifiedChain(dec);
                    }
                }
            };

            ethereum.on('accountsChanged', handleAccountsChanged);
            ethereum.on('chainChanged', handleChainChanged);
        };
        
        setupListeners();
        
        // Cleanup listeners on unmount
        return () => {
            if (ethereum && handleAccountsChanged && handleChainChanged) {
                try {
                    ethereum.removeListener('accountsChanged', handleAccountsChanged);
                    ethereum.removeListener('chainChanged', handleChainChanged);
                } catch (e) {
                    console.error('Error removing listeners:', e);
                }
            }
        };
    }, []);

    // Force light theme on mount and clean up legacy preferences
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', 'light');
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                window.localStorage.removeItem('theme');
            } catch (error) {
                console.warn('[Theme] Unable to clear legacy theme preference:', error);
            }
        }
    }, []);

    // Reset banner visibility when wallet disconnects
    useEffect(() => {
        if (!userAccount) {
            setBannerVisible(true);
        }
    }, [userAccount]);


    const checkWalletConnection = async () => {
        // 🔒 Do NOT auto-reconnect if user explicitly disconnected (persists across refreshes)
        if (isWalletDisconnected()) {
            console.log('[Guard] checkWalletConnection blocked: user explicitly disconnected');
            return;
        }
        
        // Never probe any provider while auto-connect is suppressed
        if (!allowConnectNow('metamask')) {
            console.log('[Guard] checkWalletConnection blocked: auto-connect suppressed');
            return;
        }

        // Check active provider (prefer activeEip1193 from custom wallets, then WalletConnect, MetaMask SDK, or MetaMask extension)
        const active = activeEip1193 || walletConnectProvider || metaMaskSDKProvider || getMetaMaskProvider();
        
        if (!active) {
            console.log('No wallet provider detected');
            setWalletDisconnected();
            return;
        }

        try {
            // Get accounts from active provider
            const accounts = active.request 
                ? await active.request({ method: 'eth_accounts' })
                : [];
            
            console.log('Found accounts:', accounts);
            if (accounts.length > 0) {
                console.log('Auto-connecting to account:', accounts[0]);
                // Test if wallet is actually unlocked by trying to get balance
                await testWalletUnlocked(accounts[0], active);
            } else {
                console.log('No accounts found, wallet not connected');
                setWalletDisconnected();
            }
        } catch (error) {
            console.error('Error checking wallet connection:', error);
            setWalletDisconnected();
        }
    };

    const testWalletUnlocked = async (address, ethereumProvider = null) => {
        // Never continue if we're in a "do not connect" state
        if (!allowConnectNow('metamask')) {
            console.log('[Guard] testWalletUnlocked blocked: auto-connect suppressed');
            setWalletDisconnected();
            return;
        }

        try {
            // Use the provided provider, or active provider (prefer activeEip1193 from custom wallets, then WalletConnect, MetaMask SDK, or MetaMask extension)
            const provider = ethereumProvider || activeEip1193 || walletConnectProvider || metaMaskSDKProvider || getMetaMaskProvider();
            
            if (!provider || !provider.request) {
                console.log('No provider available for unlock test');
                setWalletDisconnected();
                return;
            }
            
            // Try to get a simple balance check instead of signing
            // This is lighter than personal_sign and doesn't require user approval
            await provider.request({
                method: 'eth_getBalance',
                params: [address, 'latest']
            });
            console.log('Wallet is unlocked, balance check passed');
            await setWalletConnected(address, 'metamask');
        } catch (error) {
            // Wallet is locked or inaccessible - don't show any account info
            console.log('Wallet is locked or inaccessible, not connecting');
            setWalletDisconnected();
        }
    };

    // Helper to set wallet connected with a custom provider (for WalletConnect/MetaMask SDK)
    const setWalletConnectedWithProvider = async (
        address,
        customProvider,
        customSigner,
        origin = 'metamask',
        options = {}
    ) => {
        console.log('[setWalletConnectedWithProvider] Called with:', {
            address,
            origin,
            forceAllow: options.forceAllow,
            hasProvider: !!customProvider,
            hasSigner: !!customSigner
        });
        
        // Always keep the ref updated
        setWalletConnectedWithProviderRef.current = setWalletConnectedWithProvider;
        
        // HARD GUARD: do nothing if user has cancelled/closed Privy and no explicit flow is active
        if (!options.forceAllow && !allowConnectNow(origin)) {
            console.log('[Guard] setWalletConnectedWithProvider blocked for', origin, ': auto-connect suppressed', {
                suppress: suppressAutoConnectRef.current,
                connectTarget: connectTargetRef.current,
                origin
            });
            return;
        }
        
        console.log('[setWalletConnectedWithProvider] Guard passed, proceeding with connection...');
        
        try {
            // Determine underlying EIP-1193 provider first
            const eip1193Provider = customProvider.provider ?? customProvider;
            
            // >>> HARD BLOCK: Phantom EVM must never connect or show
            if (isPhantomProvider(eip1193Provider)) {
                console.warn('[Guard] Blocking Phantom provider from connecting');
                showNotification('Phantom (EVM) is not supported. Please use MetaMask or Privy.', 'error');
                await setWalletDisconnected(); // ensure UI is clean
                return;
            }
            
            // If this is the same account and we're fully wired, skip reconnect
            if (userAccount === address && provider && signer) {
                console.log('[setWalletConnectedWithProvider] Same account already connected, skipping reconnection');
                return;
            }
            
            console.log('[setWalletConnectedWithProvider] Setting up new connection...', {
                previousAccount: userAccount,
                newAccount: address,
                origin
            });
            
            // Now it's safe to clear old state and set new account
            const previousAccount = userAccount;
            console.log('[Account Change] Switching from',
                previousAccount ? `${previousAccount.slice(0, 6)}...${previousAccount.slice(-4)}` : 'none',
                'to', `${address.slice(0, 6)}...${address.slice(-4)}`
            );
            
            setAccountData(null);
            setHasActiveAccount(false);
            setAccountExists(false);
            setAccountCoreSynced(false);
            setConfidentialBalance(null);
            setPendingBalance(null);
            setSecureClaimInFlightByKey({});
            setDerivedKeys(null);
            setKeyBannerVisible(false);
            
            setUserAccount(address);
            setActiveWalletOrigin(origin);
            persistLastWallet(origin);
            setWalletTabActive(); // Mark tab as active so wallet persists on refresh
            clearWalletDisconnectedFlag(); // Clear disconnected flag since user is now connected
            suppressAutoConnectRef.current = false;
            
            setProvider(customProvider);
            setSigner(customSigner);
            setActiveEip1193(eip1193Provider);

            // Detect chain and pick the correct contract
            const { chainId } = await customProvider.getNetwork();
            // Ensure chainId is a number (coerce to avoid string comparison issues)
            const chainIdNum = Number(chainId);
            setCurrentChainId(chainIdNum);
            
            const chainEntry = Object.entries(CHAINS).find(([, cfg]) => cfg.chainIdDecimal === chainIdNum);
            const chainCfg = chainEntry?.[1];
            const chainKey = chainEntry?.[0];

            console.log('Provider and signer initialized for account:', address);
            console.log('Detected chain:', chainCfg?.name || 'Unknown', 'chainId:', chainId);

            // Build contract for this chain (if configured)
            const addr = chainCfg?.contractAddress || null;
            const newContract = addr ? new ethers.Contract(addr, CONTRACT_ABI, customSigner) : null;
            setContract(newContract);

            if (!addr) {
                console.warn('No contract address configured for', chainCfg?.name || 'this chain');
                // showNotification(
                //     chainCfg?.name 
                //         ? `Please add ${chainCfg.name} to your wallet first, then try connecting again.`
                //         : `Please add a supported network to your wallet or switch to a supported network.`,
                //     'warning'
                // );
            } else {
                console.log('Contract address:', addr, 'for', chainCfg?.name);
            }

            // Check if user has Stabletrust account
            if (newContract) {
                try {
                    let core;
                    try {
                        const coreTuple = await newContract.getAccountCore(address);
                        console.log('Account core loaded:', coreTuple);
                        
                        core = {
                            exists: coreTuple[0],
                            finalized: coreTuple[1],
                            pendingAction: coreTuple[2],
                            txId: coreTuple[3]?.toString() || '0',
                            elgamalPubkey: coreTuple[4],
                            pendingCreditCounter: coreTuple[5]?.toString() || '0',
                            minimumPendingCreditCounter: coreTuple[6]?.toString() || '0',
                            availableShare: '0x',
                            pendingShare: '0x',
                        };
                    } catch (getAccountError) {
                        console.log('getAccountCore failed, trying debug function...');
                        const debugData = await newContract.debugCanSubmitFlags(address);
                        
                        core = {
                            exists: debugData[0],
                            finalized: debugData[1],
                            pendingAction: debugData[2],
                            txId: '0',
                            elgamalPubkey: '0x',
                            availableShare: '0x',
                            pendingShare: '0x',
                            pendingCreditCounter: '0',
                            minimumPendingCreditCounter: '0'
                        };
                    }
                    
                    const active = core.exists && core.finalized;
                    setHasActiveAccount(active);
                    setAccountExists(core.exists);
                    
                    // Always set accountData to reflect current account state
                    // This ensures pendingAction and other flags are always current
                    setAccountData(core);
                } catch (error) {
                    console.error('Error checking account status:', error);
                    setHasActiveAccount(false);
                } finally {
                    setAccountCoreSynced(true);
                }
            } else {
                setAccountCoreSynced(true);
            }

            // Only show connection notification if this is a different account or first connection
            if (lastConnectedAccount !== address) {
               // showNotification(`Connected to account: ${formatAddress(address)}`, 'success');
                setLastConnectedAccount(address);
            }
        } catch (error) {
            console.error('Error setting up wallet connection:', error);
            setAccountCoreSynced(true);
            showNotification('Failed to complete wallet connection.', 'error');
        }
    };

    // Set the ref immediately after render (useLayoutEffect runs synchronously after DOM updates)
    // This ensures the ref is available for the restore effect
    useLayoutEffect(() => {
        setWalletConnectedWithProviderRef.current = setWalletConnectedWithProvider;
        console.log('[WalletPersist] setWalletConnectedWithProvider ref initialized via useLayoutEffect');
    });

    const setWalletConnected = async (address, origin = 'metamask') => {
        // Store in ref so it can be accessed by ensurePrivyConnection
        setWalletConnectedRef.current = setWalletConnected;
        
        // Ignore if Privy flow is active and this is not a Privy-origin event
        if (connectTargetRef.current === CONNECT_TARGETS.PRIVY && origin !== 'privy') {
            console.log('[Guard] setWalletConnected ignored during Privy flow');
            return;
        }

        if (
            isStellarBrowserWalletOrigin(connectTargetRef.current) &&
            origin !== connectTargetRef.current
        ) {
            console.log('[Guard] setWalletConnected ignored during Stellar wallet flow');
            return;
        }
        
        // HARD GUARD: do nothing if user has cancelled/closed Privy and no explicit flow is active
        if (!allowConnectNow(origin)) {
            console.log('[Guard] setWalletConnected blocked for', origin, ': auto-connect suppressed');
            return;
        }
        
        // If a non-MetaMask flow is active, disallow fallback to MetaMask
        if (origin === 'metamask' && connectTargetRef.current && connectTargetRef.current !== CONNECT_TARGETS.METAMASK) {
            if (!activeEip1193Ref.current) {
                console.log('[setWalletConnected] Blocked: active flow is not MetaMask and no active provider set yet');
                return;
            }
        }
        
        try {
            // Same Stellar pubkey: skip full reconnect only if the Soroban reader is already wired.
            // Otherwise a failed first `createStellarGatewayContractFacade` left `userAccount` set but
            // `contract` null — returning here would never fix it (user sees wallet connected, no contract).
            if (userAccount === address && isStellarBrowserWalletOrigin(origin)) {
                const stellarCfg = CHAINS.stellar;
                const readerOk =
                    contract &&
                    contract.isStellarSoroban &&
                    typeof contract.getAccountCore === 'function' &&
                    stellarCfg?.contractAddress &&
                    contract.address === stellarCfg.contractAddress;
                if (readerOk) {
                    console.log('Same Stellar wallet account, Soroban reader already wired — skipping reconnection');
                    return;
                }
                if (stellarCfg?.contractAddress) {
                    try {
                        const stellarContract = await createStellarGatewayContractFacade({
                            rpcUrl: stellarCfg.rpcUrl,
                            networkPassphrase: stellarCfg.networkPassphrase,
                            contractId: stellarCfg.contractAddress,
                            publicKey: address,
                        });
                        setContract(stellarContract);
                        console.log(
                            '[Stellar] Wired Soroban confidential-mirror reader (same account retry)',
                            stellarCfg.contractAddress
                        );
                    } catch (rewireErr) {
                        console.error('[Stellar] Soroban reader wiring failed (same-account retry):', rewireErr);
                        setContract(null);
                    }
                    return;
                }
                console.warn('[Stellar] No CHAINS.stellar.contractAddress — cannot wire Soroban reader');
                return;
            }
            if (userAccount === address && provider && signer) {
                console.log('Same account, skipping reconnection');
                return;
            }

            if (isStellarBrowserWalletOrigin(origin)) {
                const stellarPreflightError = await validateStellarAccountPreflight(address);
                if (stellarPreflightError) {
                    if (stellarPreflightError === 'NO_STELLAR_TESTNET_ACCOUNT') {
                        showNotification(
                            <>
                                No Stellar Testnet account was found for this wallet.{' '}
                                <a
                                    href={STELLAR_LAB_FUND_ACCOUNT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                >
                                    Fund your testnet account
                                </a>{' '}
                                then reconnect.
                            </>,
                            'error'
                        );
                    } else {
                        showNotification(stellarPreflightError, 'error');
                    }
                    return;
                }
            }

            // IMPORTANT: Clear all account-specific state when switching accounts
            // This prevents stale data from the previous account from persisting
            const previousAccount = userAccount;
            console.log('[Account Change] Switching from', previousAccount ? `${previousAccount.slice(0, 6)}...${previousAccount.slice(-4)}` : 'none', 'to', `${address.slice(0, 6)}...${address.slice(-4)}`);
            console.log('[Account Change] Clearing previous account state and keys...');
            setAccountData(null);
            setHasActiveAccount(false);
            setAccountExists(false);
            setAccountCoreSynced(false);
            setConfidentialBalance(null);
            setPendingBalance(null);
            setSecureClaimInFlightByKey({});
            setDerivedKeys(null); // Keys will be re-derived for the new account
            setKeyBannerVisible(false);

            setUserAccount(address);
            setActiveWalletOrigin(origin);
            persistLastWallet(origin);
            setWalletTabActive(); // Mark tab as active so wallet persists on refresh
            clearWalletDisconnectedFlag(); // Clear disconnected flag since user is now connected
            console.log('[Account Change] New account set. Keys will be automatically re-derived via useDeterministicElGamalKeypair hook.');
            
            // Clear suppression on successful wallet connection
            suppressAutoConnectRef.current = false;

            if (isStellarBrowserWalletOrigin(origin)) {
                setProvider(null);
                setSigner(null);
                setActiveEip1193(null);
                setWalletConnectProvider(null);
                setMetaMaskSDKProvider(null);

                const stellarCfg = CHAINS.stellar;
                setCurrentChainId(
                    stellarCfg?.chainIdDecimal !== undefined && stellarCfg?.chainIdDecimal !== null
                        ? Number(stellarCfg.chainIdDecimal)
                        : 0
                );

                if (stellarCfg?.contractAddress) {
                    try {
                        const stellarContract = await createStellarGatewayContractFacade({
                            rpcUrl: stellarCfg.rpcUrl,
                            networkPassphrase: stellarCfg.networkPassphrase,
                            contractId: stellarCfg.contractAddress,
                            publicKey: address,
                        });
                        setContract(stellarContract);

                        let core;
                        try {
                            const coreTuple = await stellarContract.getAccountCore(address);
                            core = {
                                exists: coreTuple[0],
                                finalized: coreTuple[1],
                                pendingAction: coreTuple[2],
                                txId: coreTuple[3]?.toString?.() || '0',
                                elgamalPubkey: coreTuple[4],
                                pendingCreditCounter: coreTuple[5]?.toString?.() || '0',
                                minimumPendingCreditCounter: coreTuple[6]?.toString?.() || '0',
                                availableShare: '0x',
                                pendingShare: '0x',
                            };
                        } catch (getAccountError) {
                            console.log('[Stellar] getAccountCore failed, trying debug function…', getAccountError);
                            try {
                                const debugData = await stellarContract.debugCanSubmitFlags(address);
                                core = {
                                    exists: debugData[0],
                                    finalized: debugData[1],
                                    pendingAction: debugData[2],
                                    txId: '0',
                                    elgamalPubkey: '0x',
                                    availableShare: '0x',
                                    pendingShare: '0x',
                                    pendingCreditCounter: '0',
                                    minimumPendingCreditCounter: '0',
                                };
                            } catch (debugErr) {
                                console.warn('[Stellar] debugCanSubmitFlags failed:', debugErr);
                                core = {
                                    exists: false,
                                    finalized: false,
                                    pendingAction: false,
                                    txId: '0',
                                    elgamalPubkey: '0x',
                                    availableShare: '0x',
                                    pendingShare: '0x',
                                    pendingCreditCounter: '0',
                                    minimumPendingCreditCounter: '0',
                                };
                            }
                        }

                        const active = core.exists && core.finalized;
                        setHasActiveAccount(active);
                        setAccountExists(!!core.exists);
                        setAccountData(core);
                    } catch (e) {
                        console.error('[Stellar] Failed to initialize Soroban contract reader:', e);
                        setContract(null);
                        setHasActiveAccount(false);
                        setAccountExists(false);
                        setAccountData(null);
                    } finally {
                        setAccountCoreSynced(true);
                    }
                } else {
                    setContract(null);
                    setHasActiveAccount(false);
                    setAccountExists(false);
                    setAccountData(null);
                    setAccountCoreSynced(true);
                }

                if (lastConnectedAccount !== address) {
                    setLastConnectedAccount(address);
                }
                return;
            }

            // Setup ethers provider and signer
            // Prefer an explicitly active EIP-1193, then WC / SDK.
            // Only fall back to window.ethereum/MetaMask when origin *is* MetaMask.
            let ethereumProvider = activeEip1193 || walletConnectProvider || metaMaskSDKProvider;

            if (!ethereumProvider && origin === 'metamask') {
                ethereumProvider = getMetaMaskProvider();
            }

            // For Privy we must never silently fall back to MetaMask.
            if (!ethereumProvider && origin === 'privy') {
                console.error('No Privy provider available in setWalletConnected; refusing to fall back to MetaMask.');
                await setWalletDisconnected();
                return;
            }
            
            // >>> BLOCK: Phantom EVM is not allowed (even if it sneaks in as activeEip1193)
            if (isPhantomProvider(ethereumProvider)) {
                console.warn('[Guard] setWalletConnected called with Phantom provider - blocking');
                showNotification('Phantom (EVM) is not supported. Please use MetaMask or Privy.', 'error');
                await setWalletDisconnected();
                return;
            }
            
            if (!ethereumProvider) {
                throw new Error('No provider available for wallet connection');
            }
            const newProvider = new ethers.providers.Web3Provider(ethereumProvider, 'any');
            const newSigner = newProvider.getSigner();
            setProvider(newProvider);
            setSigner(newSigner);
            
            // Update active EIP-1193 provider if we're using a new one
            if (!activeEip1193) {
                setActiveEip1193(ethereumProvider);
            }

            // Detect chain and pick the correct contract
            const { chainId } = await newProvider.getNetwork();
            // Ensure chainId is a number (coerce to avoid string comparison issues)
            const chainIdNum = Number(chainId);
            setCurrentChainId(chainIdNum); // Update current chain ID
            
            const chainEntry = Object.entries(CHAINS).find(([, cfg]) => cfg.chainIdDecimal === chainIdNum);
            const chainCfg = chainEntry?.[1];
            const chainKey = chainEntry?.[0];

            console.log('Provider and signer initialized for account:', address);
            console.log('Detected chain:', chainCfg?.name || 'Unknown', 'chainId:', chainId);

            // Build contract for this chain (if configured)
            const addr = chainCfg?.contractAddress || null;
            const newContract = addr ? new ethers.Contract(addr, CONTRACT_ABI, newSigner) : null;
            setContract(newContract);

            if (!addr) {
                console.warn('No contract address configured for', chainCfg?.name || 'this chain');
                showNotification(
                    chainCfg?.name 
                        ? `Please add ${chainCfg.name} to your wallet first, then try connecting again.`
                        : `Please add a supported network to your wallet or switch to a supported network.`,
                    'warning'
                );
            } else {
                console.log('Contract address:', addr, 'for', chainCfg?.name);
            }

            // Check if user has Stabletrust account (exists && finalized)
            // Only if contract is available
            if (newContract) {
                try {
                    let core;
                    try {
                        const coreTuple = await newContract.getAccountCore(address);
                    console.log('Account core loaded:', coreTuple);
                    
                    // Convert tuple to object format
                    core = {
                        exists: coreTuple[0],
                        finalized: coreTuple[1],
                        pendingAction: coreTuple[2],
                        txId: coreTuple[3]?.toString() || '0',
                        elgamalPubkey: coreTuple[4],
                        pendingCreditCounter: coreTuple[5]?.toString() || '0',
                        minimumPendingCreditCounter: coreTuple[6]?.toString() || '0',
                        availableShare: '0x',
                        pendingShare: '0x',
                    };
                } catch (getAccountError) {
                    console.log('getAccountCore failed, trying debug function...');
                    const debugData = await newContract.debugCanSubmitFlags(address);
                    console.log('Debug data loaded:', debugData);
                    
                    // Convert debug data to account data format
                    core = {
                        exists: debugData[0],
                        finalized: debugData[1],
                        pendingAction: debugData[2],
                        txId: '0', // Not available in debug function
                        elgamalPubkey: '0x', // Not available in debug function
                        availableShare: '0x', // Not available in debug function
                        pendingShare: '0x', // Not available in debug function
                        pendingCreditCounter: '0', // Not available in debug function
                        minimumPendingCreditCounter: '0' // Not available in debug function
                    };
                }
                
                const active = core.exists && core.finalized;
                setHasActiveAccount(active);
                setAccountExists(core.exists);
                console.log('Has active Stabletrust account:', active);

                // Always set accountData to reflect current account state
                // This ensures pendingAction and other flags are always current
                setAccountData(core);
                } catch (error) {
                    console.error('Error checking account status:', error);
                    // Don't fail the whole connection if account check fails
                    setHasActiveAccount(false);
                } finally {
                    setAccountCoreSynced(true);
                }
            } else {
                // No contract configured for this chain
                console.log('No contract available, skipping account check');
                setHasActiveAccount(false);
                setAccountExists(false);
                setAccountCoreSynced(true);
            }

            // Only show connection notification if this is a different account or first connection
            if (lastConnectedAccount !== address) {
              //  showNotification(`Connected to account: ${formatAddress(address)}`, 'success');
                setLastConnectedAccount(address);
            }

        } catch (error) {
            console.error('Error setting up wallet connection:', error);
            setAccountCoreSynced(true);

            // Only show error for specific connection failures, not for temporary issues
            if (error.code === 'ACTION_REJECTED' || error.code === 'UNSUPPORTED_OPERATION') {
                // User rejected the connection or operation not supported
                showNotification('Wallet connection was rejected or not supported.', 'error');
            } else if (error.message && error.message.includes('User denied')) {
                // User denied the connection
                showNotification('Wallet connection was denied.', 'error');
            } else {
                // For other errors (network issues, etc.), don't show error notification
                // as they might be temporary and the connection could still succeed
                console.log('Connection error (may be temporary):', error.message);
            }
        }
    };

    // Keep restore / background callers pointed at the latest `setWalletConnected` (with current `contract` closure).
    useLayoutEffect(() => {
        setWalletConnectedRef.current = setWalletConnected;
    });

    const setWalletDisconnected = async () => {
        // Store in ref so it can be accessed by ensurePrivyConnection
        setWalletDisconnectedRef.current = setWalletDisconnected;
        
        // 1) Unregister all known handlers BEFORE clearing refs
        try {
            for (const [prov] of providerHandlersRef.current.entries()) {
                unregisterHandlers(prov);
            }
            providerHandlersRef.current.clear?.();
        } catch (e) {
            console.error('Error unregistering handlers:', e);
        }
        
        // 2) Now clear state/refs
        setUserAccount(null);
        setProvider(null);
        setSigner(null);
        setContract(null);
        setHasActiveAccount(false);
        setAccountExists(false);
        setAccountData(null);
        setAccountCoreSynced(true);
        setActiveEip1193(null);
        setActiveWalletOrigin(null);
        clearLastWallet();
        clearWalletTabActive(); // Clear session flag when disconnecting
        setWalletDisconnectedFlag(); // Set disconnected flag to prevent auto-reconnect on refresh
        
        // Clear chain/token selection when wallet disconnects
        try {
            sessionStorage.removeItem('selectedChainId');
            sessionStorage.removeItem('selectedTokenId');
            console.log('[WalletDisconnect] Cleared chain/token selection');
        } catch (e) {
            console.warn('[WalletDisconnect] Failed to clear chain/token selection:', e);
        }
        setKeyBannerVisible(false);
        setDerivedKeys(null);
        setNotifications([]);
        setLastConnectedAccount(null);
        setLastNotifiedChain(null);

        // If we’re disconnecting while Privy was active, remember that this was manual
        // so we don’t auto-reconnect the embedded wallet in the background.
        if (activeWalletOriginRef.current === CONNECT_TARGETS.PRIVY) {
            manualPrivyDisconnectRef.current = true;
        }

        // 🔒 Block any background / auto-connect after an explicit disconnect.
        // Explicit connect flows (Privy, MetaMask, etc.) still work because they set connectTarget
        // and/or use `forceAllow`.
        suppressAutoConnectRef.current = true;
        
        // 3) Disconnect WC if present, clear SDK, clear key cache
        if (walletConnectProvider) {
            try {
                await disconnectWalletConnect();
            } catch (e) {
                console.error('Error disconnecting WalletConnect:', e);
            }
            setWalletConnectProvider(null);
        }
        
        if (metaMaskSDKProvider) {
            setMetaMaskSDKProvider(null);
        }
        
        try {
            if (secureKeyCache && typeof secureKeyCache.clearAll === 'function') {
                secureKeyCache.clearAll();
            }
        } catch (_) {}

        try {
            await disconnectStellarWalletSession();
        } catch (e) {
            console.warn('[WalletDisconnect] Stellar Wallets Kit disconnect failed:', e);
        }
    };

    // Force the UI/app back to the fully disconnected state and keep auto-connect blocked
    const resetToDisconnected = async () => {
        try {
            await setWalletDisconnected(); // you already clean handlers, providers, state here
        } finally {
            suppressAutoConnectRef.current = true; // keep blocking any background attach
            updateConnectTarget(CONNECT_TARGETS.NONE); // no active flow
        }
    };

    const forceReconnect = async () => {
        console.log('Force reconnecting wallet...');
        setWalletDisconnected();
        await connectWallet();
    };

    const refreshAccountStatus = async () => {
        console.log('Refreshing account status...');
        
        // For Privy wallets, ensure connection is maintained after operations like account creation
        const shouldTryPrivyReconnect = !manualPrivyDisconnectRef.current;
        const isPrivyConnected = shouldTryPrivyReconnect
            ? await ensurePrivyConnection()
            : false;
        
        if (userAccount && contract) {
            await checkConfidentialAccount();
        } else {
            // Do not auto-connect to any cached/default wallet.
            // Respect user's explicit wallet choice from the selection modal.
            console.log('No active wallet session; skipping auto-connect.');
        }
    };

    const fetchAccountCoreData = useCallback(async (addr, providerOverride) => {
        if (!contract && !providerOverride) {
            return null;
        }
        
        const contractAddress = contract?.address;
        if (!contractAddress) {
            return null;
        }

        // Soroban reads must not use an ethers.JsonRpcProvider against a Soroban RPC URL.
        if (contract?.isStellarSoroban) {
            if (providerOverride) {
                return null;
            }
            try {
                return await contract.getAccountCore(addr);
            } catch (_) {
                return null;
            }
        }
        
        // Guard: providerOverride must be a valid provider
        if (providerOverride && (!providerOverride.request && !providerOverride.getNetwork)) {
            console.warn('Invalid providerOverride in fetchAccountCoreData');
            return null;
        }
        
        const reader = providerOverride
            ? new ethers.Contract(contractAddress, CONTRACT_ABI, providerOverride)
            : contract;
        
        if (!reader) {
            return null;
        }
        
        try {
            return await reader.getAccountCore(addr);
        } catch (e) {
            return null;
        }
    }, [contract]);

    const checkConfidentialAccount = useCallback(async () => {
        if (!contract || !userAccount) return;

        // Store in ref so it can be accessed by ensurePrivyConnection
        checkConfidentialAccountRef.current = checkConfidentialAccount;

        try {
            // Try primary, then fallback
            let coreTuple = await fetchAccountCoreData(userAccount);
            if (!coreTuple && !contract?.isStellarSoroban) {
                const rpc = resolveRpcUrl(currentChainId);
                if (rpc) {
                    const fallbackProvider = new ethers.providers.JsonRpcProvider(rpc);
                    coreTuple = await fetchAccountCoreData(userAccount, fallbackProvider);
                }
            }
            // As a last resort, use debug flags
            if (!coreTuple) {
                try {
                    const debugData = await contract.debugCanSubmitFlags(userAccount);
                    coreTuple = [debugData[0], debugData[1], debugData[2], '0', '0x', '0x', '0x', '0', '0'];
                } catch (_) {
                    coreTuple = null;
                }
            }
            if (!coreTuple) {
                setHasActiveAccount(false);
                setAccountExists(false);
                setAccountData(null);
                setPendingActionKind(null);
                return;
            }

            const hasAccount = coreTuple[0] && coreTuple[1];
            setHasActiveAccount(hasAccount);
            setAccountExists(!!coreTuple[0]);

            const formattedAccountData = {
                exists: coreTuple[0],
                finalized: coreTuple[1],
                pendingAction: coreTuple[2],
                txId: coreTuple[3]?.toString?.() || '0',
                elgamalPubkey: coreTuple[4],
                pendingCreditCounter: coreTuple[5]?.toString?.() || '0',
                minimumPendingCreditCounter: coreTuple[6]?.toString?.() || '0',
                availableShare: '0x',
                pendingShare: '0x',
            };
            setAccountData(formattedAccountData);
            
            // Check pending action kind if there's a pending action
            if (coreTuple[2]) {
                try {
                    const pendingActionTuple = await contract.getPendingAction(userAccount);
                    const kindRaw = pendingActionTuple[0];
                    // Convert BigNumber to regular number for consistent comparison across chains
                    const kind = kindRaw?.toNumber ? kindRaw.toNumber() : Number(kindRaw);
                    setPendingActionKind(kind);
                } catch (error) {
                    console.error('Error fetching pending action kind:', error);
                    setPendingActionKind(null);
                }
            } else {
                setPendingActionKind(null);
            }
        } catch (error) {
            console.error('Error checking Stabletrust account:', error);
            setHasActiveAccount(false);
            setAccountExists(false);
            setAccountData(null);
            setPendingActionKind(null);
        }
    }, [contract, userAccount, currentChainId, fetchAccountCoreData]);

    useEffect(() => {
        checkConfidentialAccountRef.current = checkConfidentialAccount;
    }, [checkConfidentialAccount]);

    // Keep userAccountRef in sync with userAccount state
    useEffect(() => {
        userAccountRef.current = userAccount;
    }, [userAccount]);

    // Keep userAccount in sync with whatever the active provider thinks the account is.
    // This is a safety net in case some wallets don't fire accountsChanged reliably.
    useEffect(() => {
        if (!provider || !signer || !activeWalletOrigin) {
            return;
        }

        let cancelled = false;
        let intervalId = null;

        const pollAccounts = async () => {
            if (cancelled) return;
            try {
                const p = providerRef.current;
                if (!p) return;

                const accounts = await p.listAccounts();
                const next = accounts?.[0];
                const current = userAccountRef.current;

                if (
                    next &&
                    current &&
                    next.toLowerCase() !== current.toLowerCase()
                ) {
                    console.log(
                        '[AccountPoll] Detected account change from',
                        current,
                        'to',
                        next
                    );

                    const origin = activeWalletOriginRef.current || CONNECT_TARGETS.METAMASK;
                    const s = signerRef.current;

                    if (setWalletConnectedWithProviderRef.current && p && s) {
                        await setWalletConnectedWithProviderRef.current(
                            next,
                            p,
                            s,
                            origin,
                            { forceAllow: true }
                        );
                    } else if (setWalletConnectedRef.current) {
                        await setWalletConnectedRef.current(next, origin);
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    console.warn('[AccountPoll] listAccounts failed:', err);
                }
            }
        };

        // immediate check + periodic
        pollAccounts();
        intervalId = setInterval(pollAccounts, 1500);

        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [provider, signer, activeWalletOrigin]);

    useEffect(() => {
        if (!contract || !userAccount) {
            return;
        }
        checkConfidentialAccount();
    }, [contract, userAccount, currentChainId, checkConfidentialAccount]);

    // Reset pending balance banner dismissed state when a new pending balance appears or increases
    // This allows the banner to reappear if a new pending balance comes in or increases
    useEffect(() => {
        const prev = prevPendingBalanceRef.current;
        const current = pendingBalance;
        
        // Only reset dismissed state if:
        // 1. Previous was null/zero and current is non-zero (new balance appeared)
        // 2. Current is greater than previous (balance increased)
        if (current != null && !isZeroish(current)) {
            if (prev == null || isZeroish(prev) || current > prev) {
                setPendingBalanceBannerDismissed(false);
            }
        }
        
        prevPendingBalanceRef.current = current;
    }, [pendingBalance]);

    // Restore the last connected wallet after a hard refresh
    useEffect(() => {
        // CRITICAL: Check if user explicitly disconnected - if so, don't auto-reconnect
        if (isWalletDisconnected()) {
            console.log('[WalletPersist] User explicitly disconnected - not restoring wallet connection');
            clearLastWallet(); // Clear localStorage since we're not restoring
            return;
        }
        
        // CRITICAL: Read wallet origin FIRST and set connectTarget IMMEDIATELY to prevent
        // MetaMask from auto-connecting before we can restore Privy
        const origin = readLastWallet();
        
        // If Privy was the last wallet, set connectTarget synchronously BEFORE any other checks
        // This prevents MetaMask event listeners from connecting
        if (origin === CONNECT_TARGETS.PRIVY) {
            console.log('[WalletPersist] Privy detected in localStorage - setting connectTarget IMMEDIATELY to block MetaMask');
            connectTargetRef.current = CONNECT_TARGETS.PRIVY;
            updateConnectTarget(CONNECT_TARGETS.PRIVY);
            manualPrivyDisconnectRef.current = false;
        }
        if (isStellarBrowserWalletOrigin(origin)) {
            console.log('[WalletPersist] Stellar extension detected - setting connectTarget to block MetaMask');
            connectTargetRef.current = origin;
            updateConnectTarget(origin);
        }
        
        // Don't restore if we're already connected
        if (userAccount && provider && signer) {
            console.log('[WalletPersist] Already connected, skipping restore');
            // Make sure session flag is set if already connected
            if (userAccount) {
                setWalletTabActive();
            }
            return;
        }

        // Check if tab was closed (sessionStorage cleared) - don't restore if tab was closed
        const tabActive = isWalletTabActive();
        if (!tabActive) {
            console.log('[WalletPersist] Tab was closed - wallet connection was cleared. Not restoring.');
            clearLastWallet(); // Clear localStorage as well since we're not restoring
            return;
        }

        console.log('[WalletPersist] Checking localStorage for last wallet. Found:', origin);
        if (!origin) {
            console.log('[WalletPersist] No last wallet found in storage - user will need to connect manually');
            return;
        }

        console.log('[WalletPersist] ✓ Last wallet found:', origin, '- Tab is active (refresh detected). Attempting to restore...');

        if (isStellarBrowserWalletOrigin(origin)) {
            const stellarRestoreTimeout = setTimeout(async () => {
                try {
                    let retries = 15;
                    while (!setWalletConnectedRef.current && retries > 0) {
                        await new Promise((resolve) => setTimeout(resolve, 100));
                        retries -= 1;
                    }
                    ensureKitInitialized();
                    const restored = await restoreStellarWallet();
                    if (!restored?.publicKey) {
                        console.warn('[WalletPersist] Stellar Wallets Kit restore skipped (no selected wallet or access denied)');
                        return;
                    }
                    suppressAutoConnectRef.current = false;
                    await setWalletConnectedRef.current(restored.publicKey, restored.origin || origin);
                } catch (err) {
                    console.error('[WalletPersist] Stellar wallet restore failed:', err);
                } finally {
                    updateConnectTarget(CONNECT_TARGETS.NONE);
                }
            }, 500);
            return () => clearTimeout(stellarRestoreTimeout);
        }

        // Privy: we already have a dedicated auto-connect flow (ensurePrivyConnection).
        // connectTarget was already set above, so just return here.
        if (origin === CONNECT_TARGETS.PRIVY) {
            console.log('[WalletPersist] Privy restore: connectTarget already set, waiting for ensurePrivyConnection');
            return;
        }

        const restore = async () => {
            try {
                // Wait for the function to be available (should be set by useLayoutEffect, but wait just in case)
                console.log('[WalletPersist] Checking if setWalletConnectedWithProvider is available...');
                let retries = 10; // Wait up to 1 second (10 * 100ms) - should be immediate due to useLayoutEffect
                while (!setWalletConnectedWithProviderRef.current && retries > 0) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    retries--;
                }

                if (!setWalletConnectedWithProviderRef.current) {
                    console.error('[WalletPersist] ✗ setWalletConnectedWithProvider not available after waiting');
                    console.error('[WalletPersist] This should not happen - useLayoutEffect should have set it');
                    return;
                }
                console.log('[WalletPersist] ✓ setWalletConnectedWithProvider is available');

                // Set connectTarget to help with MetaMask restore (even though forceAllow should bypass)
                if (origin === CONNECT_TARGETS.METAMASK) {
                    connectTargetRef.current = CONNECT_TARGETS.METAMASK;
                    updateConnectTarget(CONNECT_TARGETS.METAMASK);
                } else if (origin === CONNECT_TARGETS.COINBASE) {
                    connectTargetRef.current = CONNECT_TARGETS.COINBASE;
                    updateConnectTarget(CONNECT_TARGETS.COINBASE);
                }

                let eip1193 = null;

                if (origin === CONNECT_TARGETS.METAMASK) {
                    // Use the strict MetaMask provider (no OKX / other imposters)
                    console.log('[WalletPersist] Discovering EIP-6963 providers for MetaMask...');
                    // Wait a bit for window.ethereum to be available
                    let waitCount = 0;
                    while (typeof window === 'undefined' || !window.ethereum) {
                        if (waitCount++ > 20) {
                            console.warn('[WalletPersist] window.ethereum not available after waiting');
                            return;
                        }
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                    try { 
                        await discoverEip6963Providers(800); 
                    } catch (e) {
                        console.warn('[WalletPersist] EIP-6963 discovery failed:', e);
                    }
                    eip1193 = getMetaMaskProvider();
                    if (!eip1193) {
                        console.warn('[WalletPersist] MetaMask provider not found');
                        return;
                    }
                    console.log('[WalletPersist] MetaMask provider found');
                } else if (origin === CONNECT_TARGETS.COINBASE) {
                    console.log('[WalletPersist] Discovering EIP-6963 providers for Coinbase...');
                    try { 
                        await discoverEip6963Providers(800); 
                    } catch (e) {
                        console.warn('[WalletPersist] EIP-6963 discovery failed:', e);
                    }
                    eip1193 = getCoinbaseWalletProvider();
                    if (!eip1193) {
                        console.warn('[WalletPersist] Coinbase provider not found');
                        return;
                    }
                    console.log('[WalletPersist] Coinbase provider found');
                } else {
                    // Trust / OKX / other wallets that you connected via WalletConnect
                    // (getWalletConnectProvider should re-use the existing WC session)
                    console.log('[WalletPersist] Attempting WalletConnect restore for:', origin);
                    eip1193 = getWalletConnectProvider();
                    if (!eip1193) {
                        console.warn('[WalletPersist] WalletConnect provider not found');
                        return;
                    }
                    console.log('[WalletPersist] WalletConnect provider found');
                }

                if (!eip1193 || !eip1193.request) {
                    console.warn('[WalletPersist] Provider missing request method');
                    return;
                }

                // Only attach if the wallet still has an authorised account for this site
                console.log('[WalletPersist] Checking for authorized accounts...');
                const accounts = await eip1193.request({ method: 'eth_accounts' });
                console.log('[WalletPersist] eth_accounts response:', accounts);
                if (!accounts || accounts.length === 0) {
                    console.warn('[WalletPersist] ✗ No authorized accounts found - user disconnected at wallet level');
                    console.warn('[WalletPersist] The wallet extension is not connected to this site. User needs to reconnect manually.');
                    return; // user is no longer "connected" at the wallet level
                }

                const addr = accounts[0];
                console.log('[WalletPersist] ✓ Found authorized account:', addr);

                // Rebuild ethers provider/signer around that EIP-1193 provider
                const ethersProvider = new ethers.providers.Web3Provider(eip1193, 'any');
                const signer = ethersProvider.getSigner();

                // This is a user-initiated session being restored,
                // so we explicitly bypass auto-connect guards here.
                suppressAutoConnectRef.current = false;
                setActiveEip1193(eip1193);

                console.log('[WalletPersist] ✓ All checks passed. Calling setWalletConnectedWithProvider...');
                console.log('[WalletPersist] Parameters:', {
                    address: addr,
                    origin: origin,
                    forceAllow: true
                });
                
                const result = await setWalletConnectedWithProviderRef.current(
                    addr,
                    ethersProvider,
                    signer,
                    origin,
                    { forceAllow: true }   // <- ignore allowConnectNow/suppression
                );
                
                console.log('[WalletPersist] ✓✓✓ Restore completed successfully! Result:', result);
                console.log('[WalletPersist] Wallet should now be connected. Check userAccount state.');
            } catch (err) {
                console.error('[WalletPersist] Silent restore failed:', err);
                console.error('[WalletPersist] Error details:', {
                    message: err.message,
                    stack: err.stack,
                    origin: origin
                });
            }
        };

        // Delay to ensure component is fully mounted and all functions are defined
        // We need to wait for setWalletConnectedWithProvider to be defined, which happens
        // when the component renders and the function is created
        const timeoutId = setTimeout(() => {
            restore();
        }, 500); // Increased delay to ensure functions are defined

        return () => clearTimeout(timeoutId);
        // We intentionally only want this to run once on mount (empty deps array is correct)
    }, []);

    const showNotification = useCallback((message, type = 'info', persistent = false) => {
        pushNotification(setNotifications, message, type, persistent);
    }, [setNotifications]);

    const hasAccountPendingAction = !!(accountData && accountData.pendingAction);

    useEffect(() => {
        if (activeWalletOrigin !== CONNECT_TARGETS.PRIVY) {
            return;
        }
        if (!contract || !userAccount) {
            return;
        }

        const hasPending = !hasActiveAccount || hasAccountPendingAction;
        if (!hasPending) {
            return;
        }

        let intervalId = null;

        const poll = async () => {
            try {
                await checkConfidentialAccount();
            } catch (err) {
                console.error('Privy poll error:', err);
            }
        };

        poll();
        intervalId = setInterval(poll, 4000);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [
        activeWalletOrigin,
        contract,
        userAccount,
        hasActiveAccount,
        hasAccountPendingAction,
        checkConfidentialAccount
    ]);


    const connectWallet = async () => {
        // Stellar-only frontend: never start the MetaMask connection path.
        if (Object.keys(NETWORK_CONFIGS).length === 0) {
            console.log('[App.js] connectWallet: Stellar-only build — opening wallet picker instead of MetaMask');
            try {
                await openWalletSelectionModal();
            } catch (e) {
                console.warn('[App.js] openWalletSelectionModal failed:', e);
            }
            return;
        }

        // CRITICAL: Do not let MetaMask flow run if the user is connecting to a different wallet
        if (connectTarget && connectTarget !== CONNECT_TARGETS.METAMASK) {
            console.log('[App.js] connectWallet blocked: different connect target active:', connectTarget);
            return;
        }
        
        updateConnectTarget(CONNECT_TARGETS.METAMASK);
        
        try {
            const isMobile = isMobileDevice();
            
            // For now, mobile is not supported - show message
            if (isMobile) {
                showNotification('Wallet connection is currently only available on desktop. Please use a desktop browser with MetaMask extension.', 'warning');
                return;
            }
            
            console.log('Connecting wallet (desktop only)...');
            
            // Desktop only: Connect via MetaMask extension
            // Get MetaMask provider STRICTLY - no fallbacks
            const ethereum = getMetaMaskProvider();
            
            if (!ethereum) {
                showNotification('MetaMask is not installed. Please install the MetaMask browser extension.', 'error');
                return;
            }
            
            // STRICT VERIFICATION: Multiple checks to ensure this is MetaMask
            console.log('MetaMask provider check:', {
                isMetaMask: ethereum.isMetaMask,
                isOKExWallet: ethereum.isOKExWallet,
                isTrust: ethereum.isTrust,
                isTrustWallet: ethereum.isTrustWallet,
                providerString: ethereum.toString(),
                hasSelectedAddress: 'selectedAddress' in ethereum,
                hasIsUnlocked: '_metamask' in ethereum
            });
            
            // Must be MetaMask
            if (!ethereum.isMetaMask) {
                showNotification('Error: Detected wallet is not MetaMask. Please ensure only MetaMask is selected.', 'error');
                return;
            }
            
            // Must NOT be OKX (even if it claims to be MetaMask)
            if (ethereum.isOKExWallet) {
                showNotification('Error: OKX Wallet detected instead of MetaMask. Please disable OKX Wallet extension or select MetaMask in your browser.', 'error');
                return;
            }
            
            // Must NOT be Trust
            if (ethereum.isTrust || ethereum.isTrustWallet) {
                showNotification('Error: Trust Wallet detected instead of MetaMask. Please ensure only MetaMask is selected.', 'error');
                return;
            }
            
            // Additional string check for OKX masquerading
            const providerStr = ethereum.toString().toLowerCase();
            if (providerStr.includes('okx') || providerStr.includes('okex')) {
                showNotification('Error: OKX Wallet detected instead of MetaMask. Please disable OKX Wallet extension.', 'error');
                return;
            }

            try {
                console.log('MetaMask provider found, initializing connection...');
                
                // FINAL VERIFICATION: Before connecting, verify this is MetaMask one more time
                // Sometimes wallets can change properties, so verify right before use
                if (!ethereum.isMetaMask || ethereum.isOKExWallet) {
                    showNotification('Error: Wallet verification failed. Please ensure MetaMask is installed and selected.', 'error');
                    return;
                }
                
                // Wait for provider to be ready using readiness probe (don't rely on private _state)
                // On mobile, this may take longer
                const maxWaitTime = isMobile ? 2000 : 500;
                let waited = 0;
                let isReady = false;
                
                while (!isReady && waited < maxWaitTime) {
                    try {
                        // Cheap readiness probe - try to get chainId
                        // Also use this to verify it's actually MetaMask responding
                        const chainId = await ethereum.request({ method: 'eth_chainId' });
                        // Re-verify after the request to ensure it's still MetaMask
                        if (ethereum.isOKExWallet) {
                            showNotification('Error: OKX Wallet detected. Please disable OKX or select MetaMask.', 'error');
                            return;
                        }
                        isReady = true;
                        console.log('Provider ready after', waited, 'ms');
                    } catch (e) {
                        // Not ready yet, wait and retry
                        await new Promise(resolve => setTimeout(resolve, 100));
                        waited += 100;
                    }
                }
                
                if (!isReady) {
                    console.warn('Provider readiness check timed out, continuing anyway');
                }
                
                // Step 1: Check if we already have permissions (cached accounts)
                let cachedAccounts = [];
                try {
                    // On mobile, add a small delay before checking accounts
                    if (isMobile) {
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                    cachedAccounts = await ethereum.request({ method: 'eth_accounts' });
                    console.log('Cached accounts:', cachedAccounts);
                } catch (e) {
                    console.log('Error getting cached accounts:', e);
                    // On mobile, if eth_accounts fails, it might mean MetaMask isn't ready
                    if (isMobile && (e.message?.includes('not available') || e.code === -32002)) {
                        console.log('MetaMask may still be initializing on mobile, retrying...');
                        await new Promise(resolve => setTimeout(resolve, 500));
                        try {
                            cachedAccounts = await ethereum.request({ method: 'eth_accounts' });
                            console.log('Cached accounts (retry):', cachedAccounts);
                        } catch (retryError) {
                            console.log('Retry also failed:', retryError);
                        }
                    }
                }
                
                // Step 2: If we have cached accounts, always trigger permission request
                // This will: show unlock popup if locked, or connect quickly if unlocked
                if (cachedAccounts.length > 0) {
                    console.log('=== Found cached account:', cachedAccounts[0]);
                    const cachedAccount = cachedAccounts[0];
                    
                    console.log('=== Requesting permissions (will trigger unlock if locked)...');
                    // Removed "Connecting to MetaMask..." notification - connection happens quickly
                    
                    try {
                        // Try wallet_requestPermissions first (if supported)
                        // This ensures unlock popup appears if wallet is locked
                        try {
                            await ethereum.request({
                                method: 'wallet_requestPermissions',
                                params: [{ eth_accounts: {} }]
                            });
                            console.log('=== Permissions granted via wallet_requestPermissions');
                        } catch (permError) {
                            // If wallet_requestPermissions is not supported (code -32004 or -32601), 
                            // fallback to eth_requestAccounts
                            if (permError.code === -32004 || permError.code === -32601 || 
                                (permError.message && permError.message.includes('not supported'))) {
                                console.log('=== wallet_requestPermissions not supported, using eth_requestAccounts fallback');
                                // Use eth_requestAccounts as fallback - this will also trigger unlock if needed
                                await ethereum.request({ method: 'eth_requestAccounts' });
                            } else {
                                // Re-throw other errors (user rejection, etc.)
                                throw permError;
                            }
                        }
                        
                        console.log('=== Permissions granted, connecting...');
                        await setWalletConnected(cachedAccount, 'metamask');
                        setTimeout(checkConfidentialAccount, 500);
                        return;
                        
                    } catch (error) {
                        console.log('=== Permission/unlock error:', error);
                        
                        if (error.code === 4001) {
                            // User cancelled - don't notify, they already know
                        } else if (error.code === -32002) {
                            // Request already pending - don't notify, MetaMask already shows this
                        } else if (error.code === -32004 || error.code === -32601) {
                            // Method not supported - try direct connection
                            console.log('=== Method not supported, attempting direct connection...');
                            try {
                                await setWalletConnected(cachedAccount, 'metamask');
                                setTimeout(checkConfidentialAccount, 500);
                                return;
                            } catch (directError) {
                                console.error('Direct connection also failed:', directError);
                                showNotification('Failed to connect. Please try again.', 'error');
                            }
                        } else {
                            showNotification('Failed to connect. Please try again.', 'error');
                        }
                        
                        // Don't show cached account if connection failed
                        setWalletDisconnected();
                        return;
                    }
                }
                
                // Step 3: No cached accounts - first time connection
                console.log('No cached accounts, requesting first-time connection...');
                // Removed connection prompt notifications - MetaMask will show its own UI
                
                try {
                    // First time - use eth_requestAccounts (shows account selection)
                    // On mobile, this may take longer as user needs to interact with MetaMask app
                    const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
                    
                    if (accounts.length === 0) {
                        console.log('No accounts returned');
                        setWalletDisconnected();
                        return;
                    }

                    const primaryAccount = accounts[0];
                    console.log('Connected to account:', primaryAccount);
                    
                    // Connect the wallet
                    await setWalletConnected(primaryAccount, 'metamask');
                } catch (connectError) {
                    // Handle different error codes and scenarios
                    if (connectError.code === 4001) {
                        // User cancelled - don't notify, they already know
                    } else if (connectError.code === -32002) {
                        // Request already pending - don't notify, MetaMask already shows this
                    } else if (connectError.code === -32004 || connectError.code === -32601) {
                        // Method not supported - already handled in permission request, but log here
                        console.log('Method not supported error:', connectError);
                        showNotification('Some wallet features may not be supported. Please try again.', 'warning');
                    } else if (connectError.message && connectError.message.includes('JSON')) {
                        // MetaMask is initializing - don't notify, user can just try again
                    } else {
                        // Platform-specific error messages
                        const isMobile = isMobileDevice();
                        const platform = isIOS() ? 'iOS' : isAndroid() ? 'Android' : isMobile ? 'mobile' : 'desktop';
                        const errorMsg = isMobile 
                            ? `Failed to connect. Make sure MetaMask app is open on ${platform}. ${connectError.message || ''}`
                            : `Failed to connect: ${connectError.message || 'Unknown error'}`;
                        showNotification(errorMsg, 'error');
                    }
                    setWalletDisconnected();
                    return;
                }

                // Do not auto-switch chains on connect; user must select a chain explicitly

                // Post-connection account check (non-blocking)
                setTimeout(checkConfidentialAccount, 500);

                // Note: accountsChanged is handled by the global setupListeners() which respects suppressAutoConnectRef
                // No need for duplicate listener here

                // Chain change is handled by the main event listener
                // (No need for additional Arbitrum-only warnings)
            } catch (error) {
                console.error('Error connecting wallet:', error);
                if (error.code === 4001) {
                    // User rejected - don't notify, they already know
                } else if (error.code === 4902) {
                    await addArbitrumSepoliaNetwork();
                } else if (error.message && (error.message.includes('JSON') || error.message.includes('parse'))) {
                    // MetaMask is initializing - don't notify, user can just try again
                } else {
                    showNotification(`Failed to connect wallet: ${error.message}`, 'error');
                }
            }
        } finally {
            // Clear connectTarget only if we were the one who set it (race-safe form)
            updateConnectTarget(prev => prev === CONNECT_TARGETS.METAMASK ? CONNECT_TARGETS.NONE : prev);
        }
    };

    const unlockWallet = async () => {
        // Since we no longer show locked state with cached accounts,
        // unlocking is the same as connecting from scratch
        console.log('Unlock wallet called, redirecting to connect wallet...');
        await connectWallet();
    };

    // Connect with Privy wallet
    // Connect with Privy wallet (single-step: login + embedded wallet connect + keys)
    const connectWithPrivy = async () => {
        if (privyLoginModalInFlightRef.current || privyWalletHookupInFlightRef.current) {
            console.log('[App.js] connectWithPrivy skipped: flow already running');
            return;
        }

        // Guard against other connect flows
        if (connectTarget && connectTarget !== CONNECT_TARGETS.PRIVY) {
            console.log('[App.js] connectWithPrivy blocked: different connect target active:', connectTarget);
            return;
        }

        let myGen = 0;
        try {
            myGen = ++privyConnectGenerationRef.current;
            const isPrivyConnectStale = () => privyConnectGenerationRef.current !== myGen;

        // We're starting a fresh explicit Privy flow: allow auto-maintenance again
        manualPrivyDisconnectRef.current = false;

            // Start clean: fully disconnected, no active provider
            await resetToDisconnected();
            if (isPrivyConnectStale()) return;
            
            // CRITICAL: Clear the disconnect flag AFTER resetToDisconnected() 
            // because resetToDisconnected() calls setWalletDisconnected() which sets the flag
            // We need to clear it so auto-connect can work (especially important for first-time signups)
            clearWalletDisconnectedFlag();
            
            // Set tab active flag early for first-time logins
            // This ensures that if something fails later and we need to retry via ensurePrivyConnection,
            // it won't be blocked by the isWalletTabActive() check
            setWalletTabActive();
            console.log('[Privy] Starting fresh connection - cleared disconnect flags and set tab active');
            
            updateConnectTarget(CONNECT_TARGETS.PRIVY);

            const waitForCondition = async (predicate, timeoutMs = 10000, intervalMs = 200) => {
                const deadline = Date.now() + timeoutMs;
                while (Date.now() < deadline) {
                    if (isPrivyConnectStale()) return false;
                    if (predicate()) {
                        return true;
                    }
                    await sleep(intervalMs);
                }
                if (isPrivyConnectStale()) return false;
                return predicate();
            };

            // 🔁 0) WAIT for Privy SDK to be ready instead of returning early
            if (!privyReadyRef.current) {
                showNotification('Initializing Privy…', 'info');
            }
            const readyOk = await waitForCondition(
                () => privyReadyRef.current,
                15000,
                250
            );
            if (!readyOk) {
                showNotification('Privy is still initializing. Please refresh and try again.', 'error');
                return;
            }

            // 1) Make sure the user is logged in to Privy (email/passkey/Google)
            if (!authenticatedRef.current) {
                privyLoginModalInFlightRef.current = true;
                try {
                    try {
                        // This opens the Privy login modal and resolves when login finishes or is cancelled
                        await login();
                    } catch (err) {
                        console.error('Privy login error:', err);
                        if (err?.code !== 4001) {
                            showNotification(`Privy login failed: ${err?.message || err}`, 'error');
                        }
                        return;
                    }
                } finally {
                    // Critical: release as soon as the Privy UI promise settles (including X on passkey chooser)
                    // so Connect is not blocked while we poll for session / wire wallets below.
                    privyLoginModalInFlightRef.current = false;
                    // handleWalletSelect holds this true for the whole connect; without clearing it here,
                    // "Connect" stays ignored until session polling finishes (~12s) after the user dismisses (X).
                    isConnectingToWalletRef.current = false;
                }

                // Passkey (and other flows) can resolve `login()` before React has applied
                // `authenticated` / ref updates. Poll access token as well so we do not treat
                // a successful session as a failed login and show a spurious error.
                // Keep this window bounded: if the user closed the modal, `login()` may still
                // resolve without a session — a long poll would otherwise feel like a frozen Connect button.
                const waitUntilPrivySessionVisible = async (timeoutMs = 12000) => {
                    const deadline = Date.now() + timeoutMs;
                    await sleep(100);
                    while (Date.now() < deadline) {
                        if (isPrivyConnectStale()) return false;
                        if (authenticatedRef.current) return true;
                        try {
                            const tok = await getAccessToken();
                            if (tok) return true;
                        } catch {
                            /* session not ready yet */
                        }
                        await sleep(200);
                    }
                    return authenticatedRef.current;
                };

                const loggedIn = await waitUntilPrivySessionVisible();
                if (isPrivyConnectStale()) return;
                if (!loggedIn) {
                    // Dismissed / abandoned login — do not imply a hard failure
                    console.log('[Privy] Login modal closed without an authenticated session');
                    return;
                }
                // Login completed successfully - continue to connect automatically
                console.log('[Privy] Login completed, proceeding with automatic wallet connection...');
                showNotification('Login successful! Connecting your wallet...', 'info');
                
                // After login, Privy may have already created an embedded wallet
                // Give it a moment and check if wallets are already available
                await sleep(500);
                if (isPrivyConnectStale()) return;
                if (privyWalletsRef.current && privyWalletsRef.current.length > 0) {
                    console.log('[Privy] Wallets already available after login:', privyWalletsRef.current.length);
                }
            }

            privyWalletHookupInFlightRef.current = true;

            // 2) Wait for wallets list to load (give extra time after login for wallet creation)
            // After first-time login, Privy may need to create the embedded wallet, so we wait longer
            const walletsReadyOk = await waitForCondition(
                () => {
                    const ready = walletsReadyRef.current;
                    if (ready && privyWalletsRef.current && privyWalletsRef.current.length > 0) {
                        // Wallets are ready and we have at least one wallet
                        return true;
                    }
                    return ready; // At least wallets are ready, even if list is empty
                },
                25000,  // Increased timeout to allow wallet creation after first login
                400     // Increased interval for faster detection
            );
            if (isPrivyConnectStale()) return;
            if (!walletsReadyOk) {
                showNotification('Privy wallets did not load. Please refresh and try again.', 'error');
                return;
            }
            
            // After login, give Privy a moment to create the embedded wallet if it doesn't exist yet
            // Refresh the wallets list to get the latest state
            if (!privyWalletsRef.current || privyWalletsRef.current.length === 0) {
                console.log('[Privy] No wallets found immediately after login, waiting for wallet creation...');
                // Wait a bit longer for Privy to create the embedded wallet after first-time login
                const walletAppeared = await waitForCondition(
                    () => privyWalletsRef.current && privyWalletsRef.current.length > 0,
                    10000, // Wait up to 10 seconds for wallet to appear
                    500    // Check every 500ms
                );
                if (walletAppeared) {
                    console.log('[Privy] Wallet appeared after waiting:', privyWalletsRef.current.length);
                } else {
                    console.log('[Privy] No wallet appeared, will try to create one');
                }
            }

            const describeWallet = (w) => ({
                address: w.address,
                walletClientType: w.walletClientType,
                walletConnector: w.walletConnector,
                walletName: w.walletName,
                isEmbedded: w.isEmbedded,
                walletIndex: w.walletIndex,
            });

            console.log(
                '[Privy wallets]',
                (privyWalletsRef.current || []).map(describeWallet)
            );

            const resolveEmbeddedWallet = (wallets) =>
                (wallets || []).find((w) => {
                    if (w?.isEmbedded) return true;
                    const clientType = w?.walletClientType?.toLowerCase?.() || '';
                    if (clientType.includes('privy')) return true;
                    const connectorType = w?.walletConnector?.type?.toLowerCase?.() || '';
                    return connectorType === 'privy' || connectorType === 'embedded';
                }) || null;

            let wallet = resolveEmbeddedWallet(privyWalletsRef.current);

            // 3) If no embedded wallet yet, create one
            if (!wallet) {
                if (createWalletRef.current) {
                    try {
                        showNotification('Creating your embedded Privy wallet...', 'info');
                        await createWalletRef.current();
                        console.log('[Privy] Wallet creation initiated, waiting for wallet to appear...');
                    } catch (createErr) {
                        console.error('Failed to create embedded Privy wallet:', createErr);
                        const alreadyHasEmbedded = typeof createErr?.message === 'string'
                            && /already has an embedded wallet/i.test(createErr.message);

                        if (!alreadyHasEmbedded) {
                            showNotification(
                                `Unable to create embedded Privy wallet: ${createErr?.message || createErr}`,
                                'error'
                            );
                            return;
                        }

                        showNotification('Detected existing embedded Privy wallet. Loading it now...', 'info');
                    }
                } else {
                    console.warn('[Privy] createWallet function not available');
                    showNotification('Unable to create Privy wallet. Please try again.', 'error');
                    return;
                }

                // Wait for the embedded wallet to appear after creation
                const embeddedReady = await waitForCondition(() => {
                    const embedded = resolveEmbeddedWallet(privyWalletsRef.current);
                    if (embedded) {
                        wallet = embedded;
                        console.log('[Privy] Embedded wallet found:', wallet.address);
                        return true;
                    }
                    return false;
                }, 25000, 400); // Increased timeout for first-time wallet creation

                if (!embeddedReady || !wallet) {
                    showNotification(
                        'No embedded Privy wallet found. Please check your Privy configuration.',
                        'error'
                    );
                    return;
                }
                console.log('[Privy] Embedded wallet ready, proceeding with connection...');
            }

            // 4) Ensure we're on the selected chain (default Arbitrum)
            const selected = sessionStorage.getItem('selectedChainId') || 'stellar';
            const targetDec = parseInt(NETWORK_CONFIGS[selected].chainId, 16);
            try {
                await wallet.switchChain(targetDec);
            } catch (switchError) {
                // Handle chain switching errors, especially for unsupported chains
                if (switchError?.message?.includes('Unsupported chainId') || 
                    switchError?.message?.includes('unsupported chain')) {
                    const chainName = NETWORK_CONFIGS[selected]?.chainName || selected;
                    showNotification(
                        `Privy does not support ${chainName} (chainId: ${targetDec}). Please use a different wallet or chain.`,
                        'error'
                    );
                    throw switchError;
                }
                throw switchError;
            }

            // 5) Get EIP-1193 provider and wire it into ethers
            const eip1193 = await wallet.getEthereumProvider();
            const provider = new ethers.providers.Web3Provider(eip1193, 'any');
            const signer = provider.getSigner();
            const accounts = await provider.listAccounts();
            if (!accounts?.length) {
                throw new Error('No accounts returned by Privy provider.');
            }

            const account = accounts[0];
            setActiveEip1193(eip1193);
            suppressAutoConnectRef.current = false; // explicit connection, allow events

            await setWalletConnectedWithProvider(
                account,
                provider,
                signer,
                'privy',
                { forceAllow: true }   // bypass auto-connect suppression guards
            );

            // 6) Listen for account/chain changes on the Privy provider
            registerHandlers(eip1193, {
                accountsChanged: (accs) => {
                    if (accs.length === 0) {
                        setWalletDisconnected();
                        return;
                    }
                    const currentProvider = provider;
                    const currentSigner = signer;
                    if (currentProvider && currentSigner) {
                        setWalletConnectedWithProvider(
                            accs[0],
                            currentProvider,
                            currentSigner,
                            'privy'
                        );
                    } else {
                        setWalletConnected(accs[0], 'privy');
                    }
                },
                chainChanged: (chainId) => {
                    const newChainId = parseInt(chainId, 16);
                    setCurrentChainId(newChainId);
                    setTimeout(checkConfidentialAccount, 1000);
                }
            }, 'privy');

            // 7) Run Stabletrust account check (drives key-derive flow)
            setTimeout(checkConfidentialAccount, 500);
            showNotification('Connected with Privy', 'success');
        } catch (err) {
            if (privyConnectGenerationRef.current !== myGen) {
                return;
            }
            console.error('Privy connect error:', err);
            await setWalletDisconnected();
            suppressAutoConnectRef.current = true;
            if (err?.code !== 4001) {
                // Check if error is due to unsupported chain
                const errorMessage = err?.message || String(err);
                if (errorMessage.includes('Unsupported chainId') || 
                    errorMessage.includes('unsupported chain')) {
                    showNotification(
                        'This chain is not supported by Privy. Please switch to a supported chain or use a different wallet.',
                        'error'
                    );
                } else {
                    showNotification(`Privy connect failed: ${errorMessage}`, 'error');
                }
            }
        } finally {
            // A newer explicit connect may be running; do not tear down its flags / connect target.
            if (privyConnectGenerationRef.current !== myGen) {
                return;
            }
            privyWalletHookupInFlightRef.current = false;
            privyLoginModalInFlightRef.current = false;
            updateConnectTarget(prev => prev === CONNECT_TARGETS.PRIVY ? CONNECT_TARGETS.NONE : prev);
            // If the user cancelled before a successful wire-up, allow another explicit connect
            // without leaving auto-connect suppressed (resetToDisconnected sets this true).
            if (!userAccountRef.current) {
                suppressAutoConnectRef.current = false;
            }
        }
    };


    // Open the native Stellar wallet picker. The Stellar Wallets Kit still
    // powers discovery + signing under the hood, but we render our own modal
    // so the UX matches the rest of the app.
    const openWalletSelectionModal = async () => {
        if (isConnectingToWalletRef.current) {
            console.log('[App.js] openWalletSelectionModal ignored: connect already in progress');
            return;
        }
        suppressAutoConnectRef.current = true;

        try {
            await resetToDisconnected();
        } catch (resetErr) {
            console.error('[App.js] Failed to reset before wallet switch:', resetErr);
            showNotification('Failed to reset previous wallet connection. Please try again.', 'error');
            return;
        }

        try {
            ensureKitInitialized();
            const supported = await listSupportedStellarWallets();
            setAvailableStellarWallets(
                Array.isArray(supported)
                    ? supported.map((w) => ({
                          id: w.id,
                          name: w.name,
                          icon: w.icon,
                          url: w.url,
                          isAvailable: w.isAvailable !== false,
                      }))
                    : []
            );
        } catch (err) {
            console.warn('[App.js] Could not load kit wallet catalog:', err);
            setAvailableStellarWallets([]);
        }

        setShowWalletSelectionModal(true);
    };

    const handleStellarWalletSelect = async (wallet) => {
        if (!wallet || !wallet.id) return;
        if (isConnectingToWalletRef.current) return;
        isConnectingToWalletRef.current = true;
        suppressAutoConnectRef.current = true;
        updateConnectTarget(kitWalletIdToAppOrigin(wallet.id) || 'stellar');

        try {
            const { address, origin } = await connectStellarWalletById(wallet.id);
            const safeOrigin = origin || 'stellar';
            await setWalletConnected(address, safeOrigin);
            setTimeout(() => {
                try { checkConfidentialAccount(); } catch (_) {}
            }, 500);
        } catch (error) {
            console.error('[App.js] Stellar wallet connection error:', error);
            const rawMsg = error?.message || error?.ext || String(error);
            let friendly = rawMsg;
            if (/no wallet has been connected/i.test(rawMsg)) {
                friendly = `${wallet.name} could not complete connection. Approve access in the wallet popup, or try again after unlocking the wallet.`;
            }
            if (!/user reject|denied|cancel|rejected|closed|dismiss/i.test(rawMsg)) {
                showNotification(friendly, 'error');
            }
            suppressAutoConnectRef.current = false;
        } finally {
            updateConnectTarget((prev) =>
                isStellarBrowserWalletOrigin(prev) ? CONNECT_TARGETS.NONE : prev
            );
            isConnectingToWalletRef.current = false;
        }
    };

    // Kept for backward compat with any external callers; delegates to the picker.
    // eslint-disable-next-line no-unused-vars
    const handleWalletSelect = async () => {
        await openWalletSelectionModal();
    };

    // Generic function to switch to any supported chain
    const switchToChain = async (chainId) => {
        const chainCfg = CHAINS[chainId];
        if (chainCfg && chainCfg.chainIdDecimal === 0) {
            try {
                sessionStorage.setItem('selectedChainId', chainId);
            } catch (err) {
                console.error('Failed to persist chain preference:', err);
            }
            console.log('Non-EVM chain; skipping MetaMask network switch:', chainId);
            return true;
        }

        // On mobile, wallets are not active, so skip wallet network switching
        // Return true silently to allow UI chain selection to proceed
        const isMobile = isMobileDevice();
        if (isMobile) {
            console.log('Skipping wallet network switch on mobile device');
            return true;
        }
        
        const networkConfig = NETWORK_CONFIGS[chainId];
        if (!networkConfig) {
            showNotification(`Network configuration not found for ${chainId}`, 'error');
            return false;
        }

        // Only use an already-connected wallet.
        // Do NOT fall back to window.ethereum / MetaMask here,
        // or MetaMask will steal focus during Privy flows.
        const active = activeEip1193 || walletConnectProvider || metaMaskSDKProvider;
        if (!active?.request) {
            // If no wallet is connected, silently allow chain selection to proceed
            // Chain switching will happen once wallet is connected
            console.log('No wallet connected, allowing chain selection to proceed');
            return true;
        }

        try {
            await active.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: networkConfig.chainId }],
            });
            console.log(`Switched to ${networkConfig.chainName}`);
            await rebuildContractForActiveChain({
                activeProvider: active,
                updateProvider: setProvider,
                updateSigner: setSigner,
                updateContract: setContract,
                updateChainId: setCurrentChainId,
                updateChainPreference: (chainKey) => {
                    try {
                        sessionStorage.setItem('selectedChainId', chainKey);
                    } catch (err) {
                        console.error('Failed to persist chain preference:', err);
                    }
                },
            });
            return true;
        } catch (error) {
            // Handle different error codes and messages
            const errorCode = error.code;
            const errorMessage = error.message?.toLowerCase() || '';
            
            // Check if it's a "chain not found" or "not connected" error
            // Error code 4902 = chain not added
            // Some wallets return different codes for unsupported chains
            const isChainNotFound = errorCode === 4902 || 
                                   errorCode === -32602 ||
                                   errorMessage.includes('not connected') ||
                                   errorMessage.includes('not found') ||
                                   errorMessage.includes('does not support') ||
                                   errorMessage.includes('unrecognized chain');
            
            if (isChainNotFound) {
                // Chain not added or not supported, try to add it
                console.log(`Chain ${networkConfig.chainName} not found, attempting to add it...`);
                try {
                    await active.request({
                        method: 'wallet_addEthereumChain',
                        params: [networkConfig],
                    });
                    console.log(`Added ${networkConfig.chainName} network`);
                    await rebuildContractForActiveChain({
                        activeProvider: active,
                        updateProvider: setProvider,
                        updateSigner: setSigner,
                        updateContract: setContract,
                        updateChainId: setCurrentChainId,
                        updateChainPreference: (chainKey) => {
                            try {
                                sessionStorage.setItem('selectedChainId', chainKey);
                            } catch (err) {
                                console.error('Failed to persist chain preference:', err);
                            }
                        },
                    });
                    
                    // After adding, try switching again
                    try {
                        await active.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{ chainId: networkConfig.chainId }],
                        });
                        console.log(`Switched to ${networkConfig.chainName} after adding`);
                        await rebuildContractForActiveChain({
                            activeProvider: active,
                            updateProvider: setProvider,
                            updateSigner: setSigner,
                            updateContract: setContract,
                            updateChainId: setCurrentChainId,
                            updateChainPreference: (chainKey) => {
                                try {
                                    sessionStorage.setItem('selectedChainId', chainKey);
                                } catch (err) {
                                    console.error('Failed to persist chain preference:', err);
                                }
                            },
                        });
                        return true;
                    } catch (switchError) {
                        console.error(`Failed to switch to ${networkConfig.chainName} after adding:`, switchError);
                        showNotification(`Added ${networkConfig.chainName} but failed to switch. Please switch manually in your wallet.`, 'warning');
                        return false;
                    }
                } catch (addError) {
                    console.error(`Failed to add ${networkConfig.chainName} network:`, addError);
                    showNotification(
                        `Failed to add ${networkConfig.chainName} network. ` +
                        `Please add it manually in your wallet settings: Chain ID ${networkConfig.chainId} (${parseInt(networkConfig.chainId, 16)})`,
                        'error'
                    );
                    return false;
                }
            } else if (errorCode === 4001) {
                // User rejected the request - don't notify, user already knows
                return false;
            } else {
                // Other errors - log and show user-friendly message
                console.error(`Error switching to ${networkConfig.chainName}:`, error);
                showNotification(
                    `Failed to switch to ${networkConfig.chainName}: ${error.message || 'Unknown error'}. ` +
                    `Please ensure the network is added in your wallet.`,
                    'error'
                );
                return false;
            }
        }
    };

    // Legacy function for backward compatibility
    const switchToArbitrumSepolia = () => switchToChain('arbitrum');

    const addArbitrumSepoliaNetwork = async () => {
        showNotification(
            'This app targets Stellar Testnet. Add the network in a Stellar wallet (for example Freighter); MetaMask EVM networks do not apply.',
            'info'
        );
    };

    // formatAddress is already defined at top level, no need for useCallback wrapper

    const handleShowKeysBanner = (keypair) => {
        if (!keypair) return;
        setDerivedKeys({
            private_key: keypair.private_key,
            public_key: keypair.public_key
        });
        setKeyBannerVisible(true);
    };

    const handleHideKeysBanner = () => {
        setKeyBannerVisible(false);
        // Don't clear derivedKeys - just hide the banner
        // The keys should remain available for the "View Keys" button
    };

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            showNotification('Copied to clipboard', 'success');
        }).catch(() => {
            showNotification('Failed to copy', 'error');
        });
    };

    // Compute the 3 visibility flags for right banner stack
    // Hide the banner if there's a pending deposit (pendingActionKind === 2) or any pending action
    const hasClaimablePendingBalance = pendingBalance != null && !isZeroish(pendingBalance);
    const showLetsGetStartedBanner =
        !!(
            userAccount &&
            currentTokenData &&
            confidentialBalance === 0 &&
            bannerVisible &&
            !hasAccountPendingAction &&
            pendingActionKind !== 2 &&
            !hasClaimablePendingBalance
        );
    
    // Check if secure claim is in-flight for the current token (same logic as header button)
    const claimKey = (userAccount && currentTokenData?.chainId && currentTokenData?.address)
        ? `${userAccount.toLowerCase()}::${currentTokenData.chainId}::${currentTokenData.address.toLowerCase()}`
        : null;
    const secureClaimInFlight = claimKey ? !!secureClaimInFlightByKey[claimKey] : false;
    
    const showPendingBanner =
        !!(currentTokenData && pendingBalance != null && !isZeroish(pendingBalance) && !pendingBalanceBannerDismissed && !secureClaimInFlight);

    if (INVITE_GATE_ENABLED && !hasAccess) {
        return <InviteGate onAccessGranted={handleAccessGranted} />;
    }

    return (
        <div className="App">
            {/* FULL-BLEED HEADER */}
            <header className="site-header">
                <div className="header-content">
                    <div className="header-left">
                        <img
                            src="/blacklogo.png"
                            alt="Logo"
                            className="brand-logo"
                        />
                        <div className="header-link-group">
                            <a
                                href="/how-it-works.html"
                                className="header-link"
                                onClick={closeMobileMenu}
                            >
                                How it works
                            </a>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="mobile-menu-toggle"
                        onClick={toggleMobileMenu}
                        aria-label="Toggle navigation menu"
                        aria-expanded={isMobileMenuOpen}
                        aria-controls="primary-navigation"
                    >
                        <i className={`fas fa-${isMobileMenuOpen ? 'times' : 'bars'}`}></i>
                    </button>
                    <div
                        className={`header-actions ${isMobileMenuOpen ? 'open' : ''}`}
                        id="primary-navigation"
                        role="navigation"
                        aria-label="Primary"
                    >
                        {/*
                          Hide the header Secure Claim button while a secure-claim request is in-flight for the
                          currently selected token, to prevent repeated signatures / duplicate requests.
                        */}
                        {(() => {
                            const claimKey = (userAccount && currentTokenData?.chainId && currentTokenData?.address)
                                ? `${userAccount.toLowerCase()}::${currentTokenData.chainId}::${currentTokenData.address.toLowerCase()}`
                                : null;
                            const inFlight = claimKey ? !!secureClaimInFlightByKey[claimKey] : false;
                            const canShow = (pendingBalance != null && !isZeroish(pendingBalance) && currentTokenData && !inFlight);
                            if (!canShow) return null;
                            return (
                        <button 
                            className="secure-claim-button" 
                            onClick={() => onSecureClaimClick?.()}
                    >
                            {`Secure Claim: ${pendingBalance !== null && pendingBalance !== undefined ? formatPlainForDisplay(pendingBalance, normalizeTokenDecimals(currentTokenData?.decimals)) : '0'} C${currentTokenData.symbol}`}
                        </button>
                            );
                        })()}
                        <div className="mobile-menu-nav-stack">
                            <a
                                href="/how-it-works.html"
                                className="header-link mobile-menu-item"
                                onClick={closeMobileMenu}
                            >
                                How it works
                            </a>
                        </div>
                        <div className="header-wallet-shelf">
                            <a
                                href={CHAINS.stellar.blockExplorerUrl}
                                className="header-stellar-live-badge header-stellar--hide-mobile"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Live on Stellar testnet (opens testnet block explorer)"
                            >
                                <span className="header-live-pulse" aria-hidden="true" />
                                <img
                                    src="/stellar.png"
                                    alt=""
                                    className="header-stellar-badge-logo"
                                    width={22}
                                    height={22}
                                />
                                <span className="header-stellar-badge-copy">
                                    Live on Stellar testnet
                                </span>
                            </a>
                            <div className="header-link-group">
                                <WalletConnection
                                    userAccount={userAccount}
                                    hasActiveAccount={hasActiveAccount}
                                    onConnect={openWalletSelectionModal}
                                    onUnlock={unlockWallet}
                                    onDisconnect={setWalletDisconnected}
                                    onRefresh={refreshAccountStatus}
                                    accountData={accountData}
                                    provider={provider}
                                    contract={contract}
                                    currentChainId={currentChainId}
                                    onShowKeysBanner={handleShowKeysBanner}
                                    keypair={derivedKeys}
                                    keyLoading={false}
                                    keyError={null}
                                    onSelectWallet={handleWalletSelect}
                                    activeWalletOrigin={activeWalletOrigin}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* PAGE CONTENT */}
            <div className="container">

                {/* Right-side stacked banners */}
                <div className="right-banner-stack" aria-label="Status banners">
                    {/* 1) Let's get started (deposit banner) */}
                    {showLetsGetStartedBanner && (
                        <div className="right-banner-card">
                            <button
                                className="right-banner-close"
                                onClick={() => setBannerVisible(false)}
                                aria-label="Close"
                            >
                                ×
                            </button>

                            <div className="right-banner-body">
                                <div className="right-banner-title">Let's get started!</div>
                                <div className="right-banner-text">
                                    You haven't deposited any {currentTokenData?.symbol || 'USDC'} yet. Click below to fund
                                    your wallet and begin.
                                </div>

                                <button
                                    className="right-banner-cta"
                                    onClick={() => {
                                        setActiveTab('deposit');
                                        setBannerVisible(false);
                                    }}
                                >
                                    Deposit Now
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 2) Pending balance */}
                    {showPendingBanner && (
                        <div className="right-banner-card pending-balance">
                            <button
                                className="right-banner-close"
                                onClick={() => setPendingBalanceBannerDismissed(true)}
                                aria-label="Close"
                            >
                                ×
                            </button>

                            <div className="right-banner-body">
                                <div className="right-banner-title">Pending balance available</div>
                                <div className="right-banner-text">
                                    You have {pendingBalance !== null && pendingBalance !== undefined ? formatPlainForDisplay(pendingBalance, normalizeTokenDecimals(currentTokenData?.decimals)) : '0'} C{currentTokenData?.symbol || ''}{' '}
                                    ready to claim. Secure it to your available balance now.
                                </div>

                                <button
                                    className="right-banner-cta"
                                    onClick={() => onSecureClaimClick?.()}
                                >
                                    Secure Claim
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {keyBannerVisible && derivedKeys && (
                    <div className="key-banner-overlay" role="dialog" aria-modal="true" aria-label="Derived Keys">
                        <div className="key-banner-card">
                            <div className="key-banner-header">
                                <h3 className="key-banner-title">Derived Keys</h3>
                                <button className="key-banner-close" aria-label="Close" onClick={handleHideKeysBanner}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="key-banner-body">
                                <div className="key-item">
                                    <label>Private Key</label>
                                    <div className="key-input-container">
                                        <input type="text" className="key-input" readOnly value={derivedKeys.private_key} />
                                        <button className="copy-button" onClick={() => copyToClipboard(derivedKeys.private_key)}>Copy</button>
                                    </div>
                                </div>
                                <div className="key-item">
                                    <label>Public Key</label>
                                    <div className="key-input-container">
                                        <input type="text" className="key-input" readOnly value={derivedKeys.public_key} />
                                        <button className="copy-button" onClick={() => copyToClipboard(derivedKeys.public_key)}>Copy</button>
                                    </div>
                                </div>
                                <div className="key-info-text">
                                    <small>Keys are deterministically derived from your wallet signature and will be the same each time you connect with this wallet.</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Notifications */}
                <div className="notifications">
                    {notifications.map(notification => (
                        <div key={notification.id} className={`notification ${notification.type}`}>
                            <i className={`fas fa-${notification.type === 'success' ? 'check-circle' : 
                                notification.type === 'error' ? 'exclamation-triangle' : 'info-circle'}`}></i>
                            <span className="notification-message">
                                {notification.message}
                            </span>
                            {notification.persistent && (
                                <button 
                                    className="notification-close"
                                    onClick={() => setNotifications((prev) => prev.filter((n) => n.id !== notification.id))}
                                    aria-label="Close notification"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    ))}
                </div>


                {/* Main Content */}
                <div className="main-content">
                    <div className={`main-content-layout ${userAccount && isZeroish(confidentialBalance) && bannerVisible ? 'with-banner' : ''}`}>
                        <div className="main-card">
                            <TabbedOperations
                                key={userAccount || 'disconnected'}
                                contract={contract}
                                userAccount={userAccount}
                                wasmModule={wasmModule}
                                provider={provider}
                                showNotification={showNotification}
                                onOperationComplete={checkConfidentialAccount}
                                onKeypairUpdate={setDerivedKeys}
                                accountData={accountData}
                                hasActiveAccount={hasActiveAccount}
                                accountExists={accountExists}
                                accountCoreSynced={accountCoreSynced}
                                onConnect={openWalletSelectionModal}
                                confidentialBalance={confidentialBalance}
                                setConfidentialBalance={setConfidentialBalance}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                onTokenDataChange={setCurrentTokenData}
                                onPendingBalanceChange={setPendingBalance}
                                onSecureClaimRegister={setOnSecureClaimClick}
                                onSecureClaimInFlightChange={handleSecureClaimInFlightChange}
                                switchToChain={switchToChain}
                                activeWalletOrigin={activeWalletOrigin}
                            />
                        </div>
                        
                        {/* Deposit banner moved to right-banner-stack */}
                    </div>
                </div>


            </div>

            {/* Native wallet picker, backed by Stellar Wallets Kit */}
            <WalletSelectionModal
                isOpen={showWalletSelectionModal}
                onClose={() => {
                    setShowWalletSelectionModal(false);
                    if (!userAccountRef.current) {
                        suppressAutoConnectRef.current = false;
                    }
                }}
                onSelectWallet={handleStellarWalletSelect}
                wallets={availableStellarWallets}
            />


            {/* Feedback Modal */}
            <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                showNotification={showNotification}
                userAccount={userAccount}
                chainId={currentChainId}
                walletType={activeWalletOrigin}
            />

            {/* FOOTER */}
            <footer className="site-footer">
                <div className="mobile-footer-stellar-wrap">
                    <a
                        href={CHAINS.stellar.blockExplorerUrl}
                        className="header-stellar-live-badge mobile-footer-stellar-badge"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Live on Stellar testnet (opens testnet block explorer)"
                    >
                        <span className="header-live-pulse" aria-hidden="true" />
                        <img
                            src="/stellar.png"
                            alt=""
                            className="header-stellar-badge-logo"
                            width={22}
                            height={22}
                        />
                        <span className="header-stellar-badge-copy">
                            Live on Stellar testnet
                        </span>
                    </a>
                </div>
                <div className="footer-content">
                    <a
                        href="https://x.com/0xfairblock?s=20"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Secured by <span className="fairblock-link">Fairblock</span>
                    </a>
                    <span className="footer-separator">•</span>
                    <button
                        className="footer-link-button"
                        onClick={() => setShowFeedbackModal(true)}
                        type="button"
                    >
                        Support / Report a Bug
                    </button>
                </div>
            </footer>
        </div>
    );
}

export default App;